import React, { useEffect, useRef, useState } from 'react';
import { X, UploadSimple, FileIcon, Lightning, FileText, ChartLine, FloppyDisk, CheckCircle, ChartBar, CalculatorIcon } from '@phosphor-icons/react';
import { Form, Input, Checkbox, DatePicker, Radio, TabsProps, Tabs, Divider, Spin, Button, message, Modal } from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import dayjs from 'dayjs';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import designServices from '../../../../services/oneButtonServices/designServices';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { DailyUsageTab } from './DailyUsageTab';
import { MonthlyUsageTab } from './MonthlyUsageTab';
import { YearlyUsageTab } from './YearlyUsageTab';
import './oneButton-css/UtilityDetailsModal.css';
import './oneButton-css/OneButtonFormControls.css';

import { utilityBillReader, extractUtilityRatesFromBill } from '../../../../services/geminiService';
import { annualKwhFromMonthlyUsageData, normalizeDailyUsageRowsFromBill, normalizeMonthlyUsageRowsFromBill } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import { UsageData, UtilityFieldConfig } from '../../../../@types/oneButtonType';
import { useBodyScrollLock } from '../../../../hooks/useBodyScrollLock';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import { UtilityDetailsOverviewPanel } from './UtilityDetailsOverviewPanel';
import UtilityLoadCalculatorModal from './UtilityLoadCalculatorModal';
import type { UtilityDetailsModalProps } from './UtilityDetailsModal.types';
import './oneButton-css/UtilityDetailsPopupModal.css';

const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;
import { syncUtilityWithRetries } from './utils/finance';
import { listSyncedIgsAndLightreachLeads } from '../../../../helpers/lightReachLeadId';

export type { UtilityDetailsModalProps } from './UtilityDetailsModal.types';

export const utilityCompanyFields: UtilityFieldConfig[] = [
    { field: 'selectState', displayText: 'State', fieldName: 'StateId', require: true, disbled: true, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCompany', displayText: 'Utility Company', fieldName: 'UtilityCompany', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCurrentRate', displayText: 'Current Rate Plan', fieldName: 'CurrentRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectProposedRates', displayText: 'Proposed/Post Install Rate Plan', fieldName: 'PostInstallRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'customerCharge', displayText: 'Customer Charge', fieldName: 'CustomerCharge', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'outstandingBalance', displayText: 'Outstanding Balance', fieldName: 'OutstandingBalance', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'selectUtilityIncrease', displayText: 'Est. Annual Utility Increase', fieldName: 'EstAnnualUtilityIncrease', require: false, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'utilityLIDRate', displayText: 'Utility LID Rate', fieldName: 'UtilityLIDRate', require: false, disbled: true, dataType: 'Checkbox', haveAccess: false, hidden: false, value: null },
];

export const utilityCompanyFieldsIGS: UtilityFieldConfig[] = [
    { field: 'selectState', displayText: 'State', fieldName: 'StateId', require: true, disbled: true, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCompany', displayText: 'Utility Company', fieldName: 'UtilityCompany', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, customClass: 'double-width', hidden: false, value: null },
    { field: 'outstandingBalance', displayText: 'Outstanding Balance', fieldName: 'OutstandingBalance', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'currentAllInUtilityRate', displayText: 'Current All In Utility Rate', fieldName: 'CurrentAllInUtilityRate', require: true, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: false, value: null },
    { field: 'utilityLIDRate', displayText: 'Utility LID Rate', fieldName: 'UtilityLIDRate', require: false, disbled: true, dataType: 'Checkbox', haveAccess: false, hidden: false, value: null },
]

export const utilityCompanyFieldsGoodLeap: UtilityFieldConfig[] = [
    { field: 'selectState', displayText: 'State', fieldName: 'StateId', require: true, disbled: true, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCompany', displayText: 'Utility Company', fieldName: 'UtilityCompany', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, customClass: 'double-width', hidden: false, value: null },
    { field: 'selectCurrentRate', displayText: 'Current Rate Plan', fieldName: 'CurrentRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectProposedRates', displayText: 'Proposed/Post Install Rate Plan', fieldName: 'PostInstallRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'customerCharge', displayText: 'Customer Charge', fieldName: 'CustomerCharge', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'outstandingBalance', displayText: 'Outstanding Balance', fieldName: 'OutstandingBalance', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'selectUtilityIncrease', displayText: 'Est. Annual Utility Increase', fieldName: 'EstAnnualUtilityIncrease', require: false, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'currentAllInUtilityRate', displayText: 'Current All In Utility Rate', fieldName: 'CurrentAllInUtilityRate', require: true, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: false, value: null },
    { field: 'utilityLIDRate', displayText: 'Utility LID Rate', fieldName: 'UtilityLIDRate', require: false, disbled: true, dataType: 'Checkbox', haveAccess: false, hidden: false, value: null },
];

export const utilityCompanyFieldsLightreach: UtilityFieldConfig[] = [
    { field: 'selectState', displayText: 'State', fieldName: 'StateId', require: true, disbled: true, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCompany', displayText: 'Utility Company', fieldName: 'UtilityCompany', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, customClass: 'double-width', hidden: false, value: null },
    { field: 'selectCurrentRate', displayText: 'Current Rate Plan', fieldName: 'CurrentRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectProposedRates', displayText: 'Proposed/Post Install Rate Plan', fieldName: 'PostInstallRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'customerCharge', displayText: 'Customer Charge', fieldName: 'CustomerCharge', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'outstandingBalance', displayText: 'Outstanding Balance', fieldName: 'OutstandingBalance', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'selectUtilityIncrease', displayText: 'Est. Annual Utility Increase', fieldName: 'EstAnnualUtilityIncrease', require: false, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'currentAllInUtilityRate', displayText: 'All IN Utility Rate', fieldName: 'CurrentAllInUtilityRate', require: true, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: false, value: null },
    { field: 'utilityLIDRate', displayText: 'Utility LID Rate', fieldName: 'UtilityLIDRate', require: false, disbled: true, dataType: 'Checkbox', haveAccess: false, hidden: false, value: null },
];

export const utilityCompanyFieldsLightreachGoodleap: UtilityFieldConfig[] = [
    { field: 'selectState', displayText: 'State', fieldName: 'StateId', require: true, disbled: true, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCompany', displayText: 'Utility Company', fieldName: 'UtilityCompany', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, customClass: 'double-width', hidden: false, value: null },
    { field: 'selectCurrentRate', displayText: 'Current Rate Plan', fieldName: 'CurrentRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectProposedRates', displayText: 'Proposed/Post Install Rate Plan', fieldName: 'PostInstallRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'customerCharge', displayText: 'Customer Charge', fieldName: 'CustomerCharge', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'outstandingBalance', displayText: 'Outstanding Balance', fieldName: 'OutstandingBalance', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'selectUtilityIncrease', displayText: 'Est. Annual Utility Increase', fieldName: 'EstAnnualUtilityIncrease', require: false, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'currentAllInUtilityRate', displayText: 'All IN Utility Rate', fieldName: 'CurrentAllInUtilityRate', require: true, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: false, value: null },
    { field: 'utilityLIDRate', displayText: 'Utility LID Rate', fieldName: 'UtilityLIDRate', require: false, disbled: true, dataType: 'Checkbox', haveAccess: false, hidden: false, value: null },
];
export const utilityCompanyFieldsEnfin: UtilityFieldConfig[] = [
    { field: 'selectState', displayText: 'State', fieldName: 'StateId', require: true, disbled: true, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCompany', displayText: 'Utility Company', fieldName: 'UtilityCompany', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectCurrentRate', displayText: 'Current Rate Plan', fieldName: 'CurrentRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'selectProposedRates', displayText: 'Proposed/Post Install Rate Plan', fieldName: 'PostInstallRatePlan', require: true, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field: 'customerCharge', displayText: 'Customer Charge', fieldName: 'CustomerCharge', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'outstandingBalance', displayText: 'Outstanding Balance', fieldName: 'OutstandingBalance', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: true, value: null },
    { field: 'selectUtilityIncrease', displayText: 'Est. Annual Utility Increase', fieldName: 'EstAnnualUtilityIncrease', require: false, disbled: false, dataType: 'Dropdown', haveAccess: false, hidden: false, value: null },
    { field:'currentAllInUtilityRate', displayText: 'All IN Utility Rate', fieldName: 'CurrentAllInUtilityRate', require: true, disbled: false, dataType: 'Textbox', haveAccess: false, hidden: false, value: null },
      
    { field: 'utilityLIDRate', displayText: 'Utility LID Rate', fieldName: 'UtilityLIDRate', require: false, disbled: true, dataType: 'Checkbox', haveAccess: false, hidden: false, value: null },

]

export const billDetailFields: any[] = [
    { field: 'billFile', displayText: 'Upload Utility Bill', require: false, disbled: false, dataType: 'FileUpload', haveAccess: false, value: null },
    { field: 'shutoffNotice', displayText: 'Shutoff Notice', require: false, disbled: false, dataType: 'radioButton', haveAccess: false, value: null },
    { field: 'utilityRate', displayText: 'Rate from Bill ($/kWh)', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, value: null },
    { field: 'meterId', displayText: 'Meter ID', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, value: null },
    { field: 'accountNumber', displayText: 'Account Number', require: false, disbled: false, dataType: 'Textbox', haveAccess: false, value: null },
    { field: 'dateofBill', displayText: 'Date of Bill', require: false, disbled: false, dataType: 'Datepicker', haveAccess: false, value: null }
];

export const dailyUsageCompanaies: string[] = [
    "Orange & Rockland - NY",
    "Orange & Rockland (ORU)",
    "PSRG",
    "PSRG - LI",
    "PSB&G",
    "PSB&G - LI",
    "PSE&G",
    "PSE&G - LI",
    "Rockland Electric",
    "Southern Maryland Electric Company (SMECO)"
];

// TODO: replace placeholder option arrays with real data wired from props or store

const plainOptions: CheckboxGroupProps<any>['options'] = [
    { label: 'Yes', value: true, className: 'sd-hero__utility-label' },
    { label: 'No', value: false, className: 'sd-hero__utility-label' }
];


const annualUsageTabs: UsageData = {
    dailyUsage: { key: 1, type: 'Daily', data: {} },
    monthlyUsage: { key: 2, type: 'Monthly', data: {} },
    yearlyUsage: { key: 3, type: 'Yearly', data: {} }
};

const UTILITY_SAVE_VALIDATION_SUMMARY =
    'Please fill or recheck all required fields and values before saving.';

/**
 * Trinity `utility-info.component` — `saveUtilityInfoFinal` skips `UpdateLoanStatus` when any
 * `Finance_Partner_Display_Name` is Lightreach.
 */
const isLightreachDisplayProduct = (opportunityDetails: any) => {
    const list = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct;
    if (!list?.length) return false;
    return list.some(
        (ele: any) => ele.Finance_Partner_Display_Name === COMMON_CONSTANTS.LightreachPartnerName
    );
};

const currentAllInFromSolar = (s: any) =>
    s == null ? undefined : (s.currentAllInUtilityRate ?? s.CurrentAllInUtilityRate);

/**
 * Trinity `utility-info.component` `saveUtilityInfoFinal` success branch — `UpdateLoanStatus` when
 * the saved `data.SolarUtility` differs from the pre-save `utilityInfo.SolarUtility` on these fields
 * and the opportunity is not a Lightreach display product.
 */
const shouldRunUpdateLoanStatusAfterUtilitySave = (newSolar: any, previousSolar: any, opportunityDetails: any) => {
    if (isLightreachDisplayProduct(opportunityDetails)) return false;
    if (!previousSolar) return false;
    return (
        newSolar.UtilityCompany != previousSolar.UtilityCompany ||
        newSolar.AnnualUsage != previousSolar.AnnualUsage ||
        newSolar.EstAnnualUtilityIncrease != previousSolar.EstAnnualUtilityIncrease ||
        newSolar.CurrentRatePlanId != previousSolar.CurrentRatePlanId ||
        newSolar.PostInstallRatePlanId != previousSolar.PostInstallRatePlanId ||
        currentAllInFromSolar(newSolar) != currentAllInFromSolar(previousSolar)
    );
};

/**
 * Trinity `utility-info.component` `UpdateLoanStatus()` + nested `getLoanStatus` (same as `GenerateQuoteService`).
 * Failures: no user-visible message. Success after getLoanStatus: optional warning, quotes refresh to pick up change-order.
 */
const runUtilitySaveUpdateLoanStatusFlow = async (
    opportunityId: string | number,
    opportunityDetails: any,
    setQuotesRefreshTrigger: (v: number) => void,
    onNewQuoteRequiredWarning: () => void
) => {
    const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct;
    let haveGoodLeapProduct = false;
    let haveLightreachProduct = false;
    (products || []).forEach((product: any) => {
        if (product.ActiveFlag && product.Finance_Partner === COMMON_CONSTANTS.GoodLeapPartnerName) haveGoodLeapProduct = true;
        if (product.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName) haveLightreachProduct = true;
    });
    if (!haveGoodLeapProduct && !haveLightreachProduct) return;
    try {
        await quoteServices.contractUpdateLoanStatus(opportunityId, 'Utility', 0);
        await quoteServices.contractGetLoanStatusByOpportunity(opportunityId);
    } catch {
        return;
    }
    try {
        const data = await quoteServices.contractGetLoanStatusByOpportunity(opportunityId);
        setQuotesRefreshTrigger(Date.now());
        if (Array.isArray(data) && data.some((detail: any) => detail.Is_Change_Order)) {
            if (!haveLightreachProduct) onNewQuoteRequiredWarning();
        }
    } catch {
        /* getLoanStatus failure: Angular only clears loader */
    }
};

const normalizeTextValue = (value: any) => (value == null ? '' : String(value));
const normalizeSelectValue = (value: any) => (value == null || value === '' ? '' : String(value));

/** Angular `utility-info` form snapshot: dropdown IDs for change-order diff / checklist. */
function readUtilityDropdownSelections(list: any[]): {
    selectCompany: string;
    selectCurrentRate: string;
    selectProposedRates: string;
} {
    const g = (field: string) => {
        const v = list.find((item: any) => item.field === field)?.value;
        return v == null || v === '' ? '' : String(v);
    };
    return {
        selectCompany: g('selectCompany'),
        selectCurrentRate: g('selectCurrentRate'),
        selectProposedRates: g('selectProposedRates'),
    };
}

function getLabelByIdForChangeOrder(lists: { value: string; label: string }[][], id: string) {
    const s = String(id ?? '');
    for (const list of lists) {
        const m = list.find((x) => String(x.value) === s);
        if (m?.label != null && String(m.label).trim() !== '') return String(m.label);
    }
    return s;
}

type UtilityChangeOrderCheckResult = {
    requiresConfirmation: boolean;
    utilityCompanyChanged: boolean;
    utilityChanges: string[];
    utilityCurrentRatePlanChanged: boolean;
    utilityInstallRatePlanChanged: boolean;
};

/** True when the opportunity/API has no selection for a dropdown (null, undefined, or empty/whitespace string). */
function isMissingDropdownSelection(value: unknown): boolean {
    if (value == null) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
}

/** First option with a non-empty value (cascading APIs may emit placeholder rows). */
function firstSelectableOptionValue(options: { value: string | number; label: string }[]): string | number | undefined {
    const found = options.find((o) => {
        const v = o?.value;
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        return true;
    });
    return found?.value;
}

function optionListContainsValue(
    options: { value: string | number; label: string }[],
    value: unknown,
): boolean {
    if (value == null || value === '') return false;
    const raw = String(value);
    return options.some((o) => String(o.value) === raw);
}

const DEFAULT_BILL_MONTH = 'January';

function electricBillMonthFromYearlyTabData(yearlyData: unknown): string {
    if (yearlyData != null && typeof yearlyData === 'object' && !Array.isArray(yearlyData)) {
        const v = (yearlyData as { ElectricBillMonth?: unknown }).ElectricBillMonth;
        return v == null || v === '' ? '' : String(v);
    }
    return '';
}

/** Build usageData + billDetails + utilityFile + annualUsageType from raw utilityDetails (e.g. solarUtilityData from store) so we don't need to call RetrieveUtilityInformation again when opening the modal. */
function buildUsageAndBillFromUtilityDetails(utilityDetails: any): { billDetails: any[]; utilityFile: any; annualUsageType: string; usageData: UsageData } {
    const su = utilityDetails?.SolarUtility ?? {};
    const sm = Array.isArray(utilityDetails?.SolarMeter) ? utilityDetails.SolarMeter[0] ?? {} : {};
    const billDetails = billDetailFields.map((field: any) => {
        switch (field.field) {
            case 'shutoffNotice': return { ...field, value: !!su.ShutoffNotice };
            case 'meterId': return { ...field, value: normalizeTextValue(sm.MeterId) };
            case 'accountNumber': return { ...field, value: normalizeTextValue(sm.AccountNumber) };
            case 'dateofBill': return { ...field, value: normalizeTextValue(sm.BillDate) };
            default: return { ...field, value: field.value ?? '' };
        }
    });
    const utilityFile = su.FilePath ? { FileName: su.FileName, FilePath: su.FilePath } : null;
    const annualUsageType = su.EnterAnnualUsage ?? 'DailyUsageCont';
    const explicitAnnualNum =
        su.AnnualUsage != null && su.AnnualUsage !== '' && Number.isFinite(Number(su.AnnualUsage)) && Number(su.AnnualUsage) > 0
            ? Number(su.AnnualUsage)
            : 0;
    const monthlyRowsForAnnual = Array.from({ length: 12 }, (_, i) => {
        const long = new Date(0, i).toLocaleString('default', { month: 'long' });
        return {
            Month: new Date(0, i).toLocaleString('default', { month: 'short' }),
            MonthUsage: su[long] ?? 0,
            Amount: su[`${long}Monthly`] ?? 0,
        };
    });
    const annualFromMonthlyDaily = annualKwhFromMonthlyUsageData(monthlyRowsForAnnual);
    const annualUsageNum =
        annualUsageType === 'MonthlyUsageCont' && annualFromMonthlyDaily > 0
            ? annualFromMonthlyDaily
            : explicitAnnualNum > 0
              ? explicitAnnualNum
              : annualFromMonthlyDaily;
    const yearlyDataFromApi = su?.YearlyUsage?.Data?.length
        ? { AnnualUsage: su.AnnualUsage, Data: su.YearlyUsage.Data }
        : { AnnualUsage: 0, Data: Array.from({ length: 12 }, (_, i) => ({ Month: new Date(0, i).toLocaleString('default', { month: 'long' }), YearlyUsage: 0 })) };
    const monthlyAnnualAmount = monthlyRowsForAnnual.reduce((s, r) => s + (Number(r.Amount) || 0), 0);
    const usageData: UsageData = {
        dailyUsage: { key: 1, type: 'Daily', data: su?.DailyUsage?.Data?.length ? { AnnualUsage: su.AnnualUsage, Data: su.DailyUsage.Data } : { AnnualUsage: 0, Data: Array.from({ length: 12 }, (_, i) => ({ Month: new Date(0, i).toLocaleString('default', { month: 'long' }), DailyUsage: 0, MonthUsage: 0, MonthAmount: 0 })) } },
        monthlyUsage: {
            key: 2,
            type: 'Monthly',
            data: {
                AnnualUsage: annualUsageNum,
                AnnualAmount: monthlyAnnualAmount,
                Data: monthlyRowsForAnnual,
            },
        },
        yearlyUsage: {
            key: 3,
            type: 'Yearly',
            data: {
                AnnualUsage: annualUsageNum,
                ElectricBillMonth: su.ElectricBillMonth ?? '',
                avgDailyUsage: annualUsageNum > 0 ? (annualUsageNum / 365).toFixed(2) : '',
            },
        },
    };
    return { billDetails, utilityFile, annualUsageType, usageData };
}

const isSolarOrBatteryFinanceProduct = (product: any) => {
    const productName = product?.Product_Type_Name ?? product?.ProductTypeName ?? product?.ProductType;
    if (productName == null || String(productName).trim() === '') return true;
    const normalizedProductName = String(productName).trim().toLowerCase();
    return [COMMON_CONSTANTS.SolarProductName, COMMON_CONSTANTS.BatteryProductName]
        .some((name) => normalizedProductName.includes(name.toLowerCase()));
};

const getFinancePartnerName = (product: any) =>
    String(
        product?.Finance_Partner ??
        product?.Finance_Partner_Display_Name ??
        product?.Finance_Partner_Name ??
        product?.FinancePartnerName ??
        product?.financePartner ??
        product?.financePartnerName ??
        ''
    ).trim();

const isSameFinancePartner = (actual: string, expected: string) => {
    const a = actual.trim().toLowerCase();
    const e = expected.trim().toLowerCase();
    return a === e || a.includes(e) || e.includes(a);
};

const hasUtilityFinancePartner = (products: any[], financePartner: string) =>
    Array.isArray(products) &&
    products.some((product: any) =>
        isSameFinancePartner(getFinancePartnerName(product), financePartner) &&
        isSolarOrBatteryFinanceProduct(product)
    );

const getUtilityCompanyFieldConfig = (products: any[] | undefined): UtilityFieldConfig[] => {
    if (hasUtilityFinancePartner(products || [], COMMON_CONSTANTS.IGSPartnerName)) return utilityCompanyFieldsIGS;
    if (hasUtilityFinancePartner(products || [], COMMON_CONSTANTS.GoodLeapPartnerName)) {
        return hasUtilityFinancePartner(products || [], COMMON_CONSTANTS.LightreachPartnerName)
            ? utilityCompanyFieldsLightreachGoodleap
            : utilityCompanyFieldsGoodLeap;
    }
    if (hasUtilityFinancePartner(products || [], COMMON_CONSTANTS.LightreachPartnerName)) return utilityCompanyFieldsLightreach;
    if (hasUtilityFinancePartner(products || [], COMMON_CONSTANTS.EnfinPartnerName)) return utilityCompanyFieldsEnfin;
    return utilityCompanyFields;
};

const buildUtilityCompanyFieldList = (utilityDetails: any, fieldConfig: UtilityFieldConfig[] = utilityCompanyFields) =>
    fieldConfig.map((field) => {
        const su = utilityDetails?.SolarUtility ?? {};
        switch (field.field) {
            case 'selectState':
                return { ...field, value: normalizeSelectValue(su.StateId) };
            case 'selectCompany':
                return { ...field, value: (su.UtilityCompany)?normalizeSelectValue(su.UtilityCompany):null };
            case 'selectCurrentRate':
                return { ...field, value: normalizeSelectValue(su.CurrentRatePlanId) };
            case 'selectProposedRates':
                return { ...field, value: normalizeSelectValue(su.PostInstallRatePlanId) };
            case 'customerCharge':
                return { ...field, value: su.CustomerCharge == null || su.CustomerCharge === 0 ? '' : normalizeTextValue(su.CustomerCharge) };
            case 'outstandingBalance':
                return { ...field, value: su.OutStandingBalance == null ? '' : normalizeTextValue(su.OutStandingBalance) };
            case 'selectUtilityIncrease':
                return { ...field, value: normalizeSelectValue(su.EstAnnualUtilityIncrease) };
            case 'currentAllInUtilityRate':
                return { ...field, value: normalizeTextValue(su.CurrentAllInUtilityRate ?? '') };
            case 'utilityLIDRate':
                return { ...field, value: !!su.UtilityLIDRate };
            default:
                return { ...field, value: field.value ?? '' };
        }
    });


const buildBillDetailFieldList = (utilityDetails: any) =>
    billDetailFields.map((field) => {
        const su = utilityDetails?.SolarUtility ?? {};
        const sm = Array.isArray(utilityDetails?.SolarMeter) && utilityDetails.SolarMeter.length > 0 ? utilityDetails.SolarMeter[utilityDetails.SolarMeter.length - 1] ?? null : null;
        switch (field.field) {
            case 'shutoffNotice':
                return { ...field, value: !!su.ShutoffNotice };
            case 'meterId':
                return { ...field, value: normalizeTextValue(sm?.MeterId) };
            case 'accountNumber':
                return { ...field, value: normalizeTextValue(sm?.AccountNumber) };
            case 'dateofBill':
                return { ...field, value: normalizeTextValue(sm?.BillDate) };
            case 'utilityRate':
                return { ...field, value: normalizeTextValue(su.Utility_Rate) };
            default:
                return { ...field, value: field.value ?? '' };
        }
    });

export const UtilityDetailsModal: React.FC<UtilityDetailsModalProps> = ({
    oppId,
    open,
    onClose,
    initialTab = 'overview',
}: UtilityDetailsModalProps) => {

    const [billExtractLoading, setBillExtractLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveResponse, setSaveResponse] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
    const [billExtractError, setBillExtractError] = useState<string | null>(null);
    /** Rate parsed from uploaded bill (Gemini); shown in Bill detail when present */
    const [imagePreviewUrls, setImagePreviewUrls] = useState<Record<string, string>>({});
    const [companyList, setCompanyList] = useState<{ value: string; label: string }[]>([]);
    const setSolarUtilityData = useOneButtonStore((s) => s.setSolarUtilityData);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const setUtilityCompanyName = useOneButtonStore((s) => s.setUtilityCompanyName);
    const setPricingNetworkRefreshTrigger = useOneButtonStore((s) => s.setPricingNetworkRefreshTrigger);
    const setUtilityDetailsDirty = useOneButtonStore((s) => s.setUtilityDetailsDirty);
    const utilityDetailsDirty = useOneButtonStore((s) => s.utilityDetailsDirty);
    const setQuotesRefreshTrigger = useOneButtonStore((s) => s.setQuotesRefreshTrigger);
    const setPriceQuoteDetails = useOneButtonStore((s) => s.setPriceQuoteDetails);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const utilityInfoAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'UI_Utility_Info_Access');
    const opportunityInfoAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Opportunity_Information_Access');
    const showUtilityLidRate = getOneButtonCapabilityValue(onebuttonCapabilities, 'Show_Utility_LID_Rate');

    const isViewOnly = utilityInfoAccess === 'View';
    const isOpportunityViewOnly = opportunityInfoAccess === 'View';
    const isUtilityLidRateDisabled = showUtilityLidRate !== 'Yes';

    const didHydrateUtilityFormRef = useRef(false);
    /** Angular `utility-info` change-order: baseline IDs + rate option lists when the modal first stabilizes. */
    const changeOrderBaselineCapturedRef = useRef(false);
    const changeOrderInitialSelectsRef = useRef<{
        selectCompany: string;
        selectCurrentRate: string;
        selectProposedRates: string;
    } | null>(null);
    const initialCurrentValArrayRef = useRef<{ value: string; label: string }[]>([]);
    const initialProposedValArrayRef = useRef<{ value: string; label: string }[]>([]);
    /** Mirrors Angular `isUtilityUpdatedFlag` — refreshed inside `checkChangeOrder` from `getLoanStatus`. */
    const isUtilityUpdatedFlagRef = useRef(false);
    /** Angular `hasShownConfirmation` — scoped to one save attempt when `pageRedirection` is false. */
    const hasShownConfirmationRef = useRef(false);

    /** Controlled change-order confirm — avoids static `Modal.confirm` stacking under `DashboardModal` (zIndex 12000). */
    const [changeOrderConfirmOpen, setChangeOrderConfirmOpen] = useState(false);
    const changeOrderConfirmResolveRef = useRef<((v: boolean) => void) | null>(null);

    const markUtilityDirty = () => {
        if (!open || !didHydrateUtilityFormRef.current || isViewOnly) return;
        setUtilityDetailsDirty(true);
        setSaveResponse((prev) =>
            prev?.type === 'error' && prev.message === UTILITY_SAVE_VALIDATION_SUMMARY ? null : prev,
        );
    };
    const [messageApi, messageContextHolder] = message.useMessage();
    const [isFileChange, setIsFileChange] = useState<boolean>(false);
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [utilityPanelError, setUtilityPanelError] = useState<string | null>(null);
    /** Primary modal sections: overview | company | bill | usage (nested Daily/Monthly/Yearly on usage tab). */
    const [mainSectionTab, setMainSectionTab] = useState<string>(initialTab);
    const [loadCalculatorOpen, setLoadCalculatorOpen] = useState(false);

    const [isShowingLoader, setLoader] = useState<boolean>(false);
    useEffect(() => {
        if (open) {
            didHydrateUtilityFormRef.current = false;
            changeOrderBaselineCapturedRef.current = false;
            changeOrderInitialSelectsRef.current = null;
            initialCurrentValArrayRef.current = [];
            initialProposedValArrayRef.current = [];
            isUtilityUpdatedFlagRef.current = false;
            hasShownConfirmationRef.current = false;
            setSaveResponse(null);
            setMainSectionTab(initialTab);
            setLoadCalculatorOpen(false);
            setIsUploading(false);
            setUploadProgress(0);
            setSaveLoading(false);
        }
    }, [open, initialTab]);

    useBodyScrollLock(open);

    useEffect(() => {
        const imageFiles = uploadFiles.filter((f) => (f.type || '').startsWith('image/'));
        const next: Record<string, string> = {};
        imageFiles.forEach((f) => {
            const key = `${f.name}-${f.size}-${f.lastModified}`;
            next[key] = URL.createObjectURL(f);
        });
        setImagePreviewUrls((prev) => {
            Object.values(prev).forEach(URL.revokeObjectURL);
            return next;
        });
        return () => {
            Object.values(next).forEach(URL.revokeObjectURL);
        };
    }, [uploadFiles]);
    const [newCurrentValArray, setNewCurrentValArray] = useState<{ value: string; label: string }[]>([]);
    const [newProposedValArray, setNewProposedValArray] = useState<{ value: string; label: string }[]>([]);
    const [utilityIncreaseArr, setUtilityIncreaseArr] = useState<{ value: string; label: string }[]>([]);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    /** Numeric opportunity ID for API calls (RetrieveUtilityInformation expects this, not Salesforce ID). */
    const opportunityID = useOneButtonStore((s) => s.opportunityID);

    const [utilityCompanyFieldList, setUtilityCompanyFieldList] = useState<any[]>([]); //utilityCompanyFields
    const [billDetailFieldList, setBillDetailFieldList] = useState<any[]>(billDetailFields);

    const [tabIndex, setTabIndex] = useState<string>('1');
    const [usageTabData, setUsageTabData] = useState<UsageData>(annualUsageTabs);

    const [utilityInformation, setUtilityInformation] = useState<any>([]);
    const [dailyUsageJson, setDailyUsageJson] = useState<any>(null);
    const [yearlyUsage, setYearlyUsage] = useState<any>(null);
    const [monthlyUsage, setMonthlyUsage] = useState<any>(null);

    const getOptions = (field: string) => {
        switch (field) {
            case "selectState": {
                const stateValue = utilityCompanyFieldList.find((item: any) => item.field === 'selectState')?.value;
                return stateValue == null || stateValue === ''
                    ? []
                    : [{ value: String(stateValue), label: String(stateValue) }];
            }
            case "selectCompany":
                return companyList;
            case "selectCurrentRate":
                return newCurrentValArray;
            case "selectProposedRates":
                return newProposedValArray;
            case "selectUtilityIncrease":
                return utilityIncreaseArr;
            case "currentAllInUtilityRate":
                return [];
            case "utilityLIDRate":
                return [];
        }
    }

    const getAnnualUsageTabs = (usageData: UsageData) => {
        if (usageData == null) return [];
        const getChildren = (tab: any) => {
            if (tab.type === 'Daily') {
                return <DailyUsageTab DailyUsage={tab.data} DailyUsageChange={handleDailyUsageChange} disabled={isViewOnly} />
            } else if (tab.type === 'Monthly') {
                return <MonthlyUsageTab MonthlyUsage={tab.data} MonthlyUsageChange={handleMonthlyUsageChange} disabled={isViewOnly} />
            } else if (tab.type === 'Yearly') {
                return (
                    <YearlyUsageTab
                        YearlyUsage={tab.data}
                        setYearlyUsage={(v) => {
                            handleYearlyUsageChange(v);
                            markUtilityDirty();
                        }}
                        disabled={isViewOnly}
                    />
                );
            }
        }
        return Object.values(usageData).map((tab) => ({
            key: String(tab.key),
            label: <span className="obd-utility-popup__tab-label">{tab.type}</span>,
            children: getChildren(tab)
        }));
    }

    const activeSfid = oppId; //|| (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) || '';
    const theme = useOneButtonStore((s) => s.theme);

    /** Fetches rate-plan and utility-increase options for a company. CascadingDropdowns API expects CellValue = company name (string), not numeric ID. */
    const loadCascadingOptionsForCompany = (companyName: string) => {
        const cellValue = String(companyName ?? '').trim();
        if (!cellValue) return;
        const data = {
            ExcelFileName: "Sunnova Utility Data",
            SheetName: "Sunnova Utility Info",
            InputValues: [{ Type: "String", CellNumber: "B4", CellValue: cellValue }],
            OutputCells: ["F4", "AH4", "AJ4", "AI4", "AK4", "S4", "AG4"]
        };
        return masterServices.cascadingDropDown(data).then((response: any) => {
            const proposedRateArr = response[1].CellValue.split('|');
            const proposedValueArr = response[2].CellValue.split('|');
            const currentRateArr = response[3].CellValue.split('|');
            const currentValueArr = response[4].CellValue.split('|');
            const utilityIncreaseArray = response[5].CellValue.split('|');
            setNewProposedValArray(proposedRateArr.map((_: string, i: number) => ({
                value: proposedValueArr[i]?.trim() ?? '',
                label: proposedRateArr[i]?.trim() ?? ''
            })));
            setNewCurrentValArray(currentRateArr.map((_: string, j: number) => ({
                value: currentValueArr[j]?.trim() ?? '',
                label: currentRateArr[j]?.trim() ?? ''
            })) as { value: string; label: string }[]);
            const increaseArr = utilityIncreaseArray.map((item: any) => ({
                value: (item === "N/A") ? "" : String(Number(item)),
                label: (item === "N/A") ? "" : Number(Number(item) * 100).toFixed(1) + '%'
            }));
            const uniqueIncrease = [...new Map(increaseArr.map((i: { value: string; label: string }) => [i.value, i])).values()] as { value: string; label: string }[];
            setUtilityIncreaseArr(uniqueIncrease);
        }).catch(() => { /* keep existing options on error */ });
    };

    /** When opportunity has no Utility Company, select the first loaded option and fetch cascading rate / increase lists. */
    useEffect(() => {
        if (!open || companyList.length === 0) return;
        setUtilityCompanyFieldList((prev) => {
            if (!prev?.length) return prev;
            const idx = prev.findIndex((f: any) => f.field === 'selectCompany');
            if (idx < 0) return prev;
            const field = prev[idx];
            if (!isMissingDropdownSelection(field.value)) return prev;
            const first = companyList[0];
            if (!first?.label) return prev;
            queueMicrotask(() => loadCascadingOptionsForCompany(String(first.label).trim()));
            const next = [...prev];
            next[idx] = { ...field, value: String(first.value) };
            return next;
        });
    }, [open, companyList]);

    /**
     * After cascading dropdown options exist: if current/proposed rate is empty or not in the
     * loaded option list (e.g. saved CurrentRatePlanId / PostInstallRatePlanId), select the first option.
     * Est. annual increase still only fills when empty.
     */
    useEffect(() => {
        if (!open) return;
        setUtilityCompanyFieldList((prev) => {
            if (!prev?.length) return prev;
            let changed = false;
            const next = prev.map((field: any) => {
                if (field.dataType !== 'Dropdown' || field.hidden) return field;
                if (field.field === 'selectCurrentRate') {
                    const first = firstSelectableOptionValue(newCurrentValArray);
                    if (first === undefined) return field;
                    if (
                        !isMissingDropdownSelection(field.value) &&
                        optionListContainsValue(newCurrentValArray, field.value)
                    ) {
                        return field;
                    }
                    changed = true;
                    return { ...field, value: String(first) };
                }
                if (field.field === 'selectProposedRates') {
                    const first = firstSelectableOptionValue(newProposedValArray);
                    if (first === undefined) return field;
                    if (
                        !isMissingDropdownSelection(field.value) &&
                        optionListContainsValue(newProposedValArray, field.value)
                    ) {
                        return field;
                    }
                    changed = true;
                    return { ...field, value: String(first) };
                }
                if (field.field === 'selectUtilityIncrease') {
                    if (!isMissingDropdownSelection(field.value)) return field;
                    const first = firstSelectableOptionValue(utilityIncreaseArr);
                    if (first === undefined) return field;
                    changed = true;
                    return { ...field, value: String(first) };
                }
                return field;
            });
            return changed ? next : prev;
        });
    }, [open, newCurrentValArray, newProposedValArray, utilityIncreaseArr]);

    /**
     * One-time baseline per modal open (Angular `initialValue` + `initialCurrentValArray` / `initialProposedValArray`)
     * after hydrate + cascading options are ready — used by `checkChangeOrderForSave`.
     */
    useEffect(() => {
        if (!open || !didHydrateUtilityFormRef.current || changeOrderBaselineCapturedRef.current) return;
        const needCurrent = utilityCompanyFieldList.some((f: any) => f.field === 'selectCurrentRate' && !f.hidden);
        const needProposed = utilityCompanyFieldList.some((f: any) => f.field === 'selectProposedRates' && !f.hidden);
        if (needCurrent && newCurrentValArray.length === 0) return;
        if (needProposed && newProposedValArray.length === 0) return;
        changeOrderBaselineCapturedRef.current = true;
        changeOrderInitialSelectsRef.current = readUtilityDropdownSelections(utilityCompanyFieldList);
        initialCurrentValArrayRef.current = newCurrentValArray.map((x) => ({ ...x }));
        initialProposedValArrayRef.current = newProposedValArray.map((x) => ({ ...x }));
    }, [open, utilityCompanyFieldList, newCurrentValArray, newProposedValArray]);

    const utlityCompanyUpdated = (companyName: any) => {
        const label = companyName?.label ?? (typeof companyName === 'string' ? companyName : '');
        if (!label) return;
        const cellValue = String(label).trim();
        let inputValues = [{ Type: "String", CellNumber: "B4", CellValue: cellValue }];
        //   let solarProduct = OpportunityProductsFinanceInfo.filter((product: any) => product.Finance_Partner == "IGS");
        //   if (solarProduct && solarProduct.length > 0) inputValues.push({ Type: "String", CellNumber: "B6", CellValue: solarProduct[0].Finance_Partner });
        let data = {
            "ExcelFileName": "Sunnova Utility Data",
            "SheetName": "Sunnova Utility Info",
            "InputValues": inputValues,
            "OutputCells": ["F4", "AH4", "AJ4", "AI4", "AK4", "S4", "AG4"]
        }

        masterServices.cascadingDropDown(data).then((response: any) => {
            let proposedRateArr = response[1].CellValue.split('|');
            let proposedValueArr = response[2].CellValue.split('|');
            let currentRateArr = response[3].CellValue.split('|');
            let currentValueArr = response[4].CellValue.split('|');
            let utilityIncreaseArray = response[5].CellValue.split('|');
            // this.utilityCompanyErrorMessage = response.data[6].CellValue;
            let newProposedValArray = [];
            for (let i = 0; i < proposedRateArr.length; i++) {
                let proposedRateObj = {
                    value: proposedValueArr[i].trim(),
                    label: proposedRateArr[i].trim()
                }
                newProposedValArray.push(proposedRateObj)
            }
            setNewProposedValArray(newProposedValArray);
            let newCurrentValArray = [];
            for (let j = 0; j < currentRateArr.length; j++) {
                let currentRateObj = {
                    value: currentValueArr[j].trim(),
                    label: currentRateArr[j].trim()
                }
                newCurrentValArray.push(currentRateObj)
            }
            setNewCurrentValArray(newCurrentValArray);

            let utilityIncreaseArr: any[] = [];
            utilityIncreaseArray.forEach((item: any) => {
                let data = {
                    value: (item == "N/A") ? "" : Number(item),
                    label: (item == "N/A") ? "" : Number(item * 100).toFixed(1) + '%'
                }
                utilityIncreaseArr.push(data);
            });
            const uniqueUtilityIncreaseArr: any[] = [...new Map(utilityIncreaseArr.map((item: { value: any; }) => [item.value, item])).values()]
            setUtilityIncreaseArr(uniqueUtilityIncreaseArr);

            setUtilityCompanyFieldList((prev) => {
                return prev.map((field: any) => {
                    if (field.dataType === "Dropdown" && (field.field === "selectCurrentRate" || field.field === "selectProposedRates")) {
                        const dropdownList = field.field === "selectCurrentRate" ? newCurrentValArray : newProposedValArray;
                        const first = firstSelectableOptionValue(dropdownList);
                        if (first === undefined) return field;
                        if (optionListContainsValue(dropdownList, field.value)) return field;
                        return { ...field, value: String(first) };
                    }
                    return field;
                });
            });
        })


    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = '';
        if (files.length === 0 || files.length > 1) return;
        ClearBillFields();
        setUploadFiles?.(files);
        const file = files[0];
        setBillExtractError(null);
        setBillExtractLoading(true);
        setIsFileChange(true);
        try {
            // const extracted = await extractUtilityBillFields(file);
            const extracted = await utilityBillReader(file);
            const extractedRates = await extractUtilityRatesFromBill(file);
            const rateValue = extractedRates.allInRate ?? extractedRates.ratePerKwh;
            if (rateValue != null && Number.isFinite(rateValue)) {
                setUtilityCompanyFieldList((prev) =>
                    prev.map((f: any) =>
                        f.field === 'currentAllInUtilityRate'
                            ? { ...f, value: String(rateValue) }
                            : f
                    )
                );
                handleBillFieldChange('utilityRate', rateValue ? String(rateValue) : null);
            };

            const updatedBillDetails = [...billDetailFieldList];
            updatedBillDetails.forEach((field: any) => {
                if (field.field === 'accountNumber') {
                    field.value = extracted.accountNumber;
                }
                if (field.field === 'dateofBill') {
                    const d = dayjs(extracted.dateOfBill, ['YYYY-MM-DD', 'MM-DD-YYYY']);
                    field.value = d.isValid() ? d.format('MM-DD-YYYY') : extracted.dateOfBill;
                }
                if (field.field === 'meterId') {
                    field.value = extracted.meterId;
                }
                if (field.field === 'utilityRate' && rateValue != null && Number.isFinite(rateValue)) {
                    field.value = String(rateValue);
                }
                if (field.field === 'shutoffNotice') {
                    field.value = extracted.shutoffNotice === 'Yes';
                }
            });
            setBillDetailFieldList(billDetailFieldList);



            if (extracted.monthlyUsage != null && extracted.monthlyUsage.length > 0) {

                let index = dailyUsageCompanaies.findIndex((company: any) => company === extracted.CompanyName);
                const months:string[] = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ];
                const months1 = [
                    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                ]
                const year = dayjs(extracted.dateOfBill, ['YYYY-MM-DD', 'MM-DD-YYYY']).year();
                if(index == -1){
                    extracted.monthlyUsage.forEach((item: any) => {
                        let days = new Date(year, months1.indexOf(item.month) + 1, 0).getDate();
                        item.value = (item.value/days).toFixed(2);
                    })
                }

                const dataRows1 = normalizeMonthlyUsageRowsFromBill(extracted.monthlyUsage as Record<string, unknown>[]);
                dataRows1.sort((a:any, b:any) => {
                    return months.indexOf(a.Month) - months.indexOf(b.Month);
                })
                const dataRows = normalizeDailyUsageRowsFromBill(extracted.monthlyUsage as Record<string, unknown>[],year);
                const annualFromDays = annualKwhFromMonthlyUsageData(dataRows1);
                const apiAnnualRaw = Number(extracted.annualUsage);
                const yearlyAnnual =
                    Number.isFinite(apiAnnualRaw) && apiAnnualRaw > 0 ? apiAnnualRaw : annualFromDays;
                const annualAmount = extracted.monthlyUsage.reduce(
                    (sum, r: any) => sum + Number(r.amount ?? r.Amount ?? 0),
                    0,
                );
                const formattedDaily = {
                    AnnualUsage: annualFromDays,
                    AnnualAmount: annualAmount,
                    Data: dataRows,
                };
                const yearlyFromMonthly = {
                    AnnualUsage: yearlyAnnual,
                    avgDailyUsage:
                        yearlyAnnual > 0 ? (Number(yearlyAnnual) / 365).toFixed(2) : '',
                    ElectricBillMonth: electricBillMonthFromYearlyTabData(usageTabData.yearlyUsage?.data),
                };

                const formattedMOnthly = {
                    AnnualUsage: annualFromDays,
                    AnnualAmount: annualAmount,
                    Data: dataRows1,
                }
                let updatedAnnualUsageTabContent = {
                    dailyUsage: { key: 1, type: 'Daily', data: formattedDaily },
                    monthlyUsage: { key: 2, type: 'Monthly', data: formattedMOnthly },
                    yearlyUsage: { key: 3, type: 'Yearly', data: yearlyFromMonthly },
                };

                setUsageTabData(updatedAnnualUsageTabContent);
                handleYearlyUsageChange(yearlyFromMonthly);
                setDailyUsageJson(formattedDaily);
                setMonthlyUsage(formattedMOnthly);

            } else {
                // No monthly rows — still populate Yearly from API AnnualUsage when present
                const apiAnnualRaw = Number(extracted.annualUsage);
                if (Number.isFinite(apiAnnualRaw) && apiAnnualRaw > 0) {
                    const yearlyFromApi = {
                        AnnualUsage: apiAnnualRaw,
                        avgDailyUsage: (apiAnnualRaw / 365).toFixed(2),
                        ElectricBillMonth: electricBillMonthFromYearlyTabData(usageTabData.yearlyUsage?.data),
                    };
                    setUsageTabData((prev) => ({
                        ...prev,
                        yearlyUsage: { key: 3, type: 'Yearly', data: yearlyFromApi },
                    }));
                    handleYearlyUsageChange(yearlyFromApi);
                }
            }
        } catch (err) {
            setBillExtractError(err instanceof Error ? err.message : 'Failed to extract data from file.');
        } finally {
            setBillExtractLoading(false);
        }
        markUtilityDirty();
    };

    const handleBillFieldChange = (field: any, value: any) => {
        const fieldKey = typeof field === 'string' ? field : field?.field;
        setBillDetailFieldList((prev) =>
            prev.map((ele: any) => (ele.field === fieldKey ? { ...ele, value } : ele)),
        );
        markUtilityDirty();
    };

    const ClearBillFields = () => {
        setBillDetailFieldList(billDetailFields.map((ele: any) => ({ ...ele, value: ele.field === 'shutoffNotice' ? false : null })));
    }

    const handleYearlyUsageChange = (nextYearly: any) => {
        setYearlyUsage(nextYearly);
        setDailyUsageJson({ AnnualUsage: nextYearly.AnnualUsage, Data: dailyUsageJson?.Data ?? [] });
    };

    const yearlyElectricBillMonthRaw = usageTabData?.yearlyUsage?.data as { ElectricBillMonth?: unknown } | undefined;
    const electricBillMonthForEffect =
        yearlyElectricBillMonthRaw != null && typeof yearlyElectricBillMonthRaw === 'object'
            ? String((yearlyElectricBillMonthRaw as { ElectricBillMonth?: unknown }).ElectricBillMonth ?? '').trim()
            : '';

    /** Yearly tab: default "Bill of Month" to first month in the dropdown when opportunity data is missing. */
    useEffect(() => {
        if (!open) return;
        if (electricBillMonthForEffect !== '') return;
        const data = usageTabData?.yearlyUsage?.data;
        if (data == null || typeof data !== 'object') return;
        const next = { ...data, ElectricBillMonth: DEFAULT_BILL_MONTH };
        setUsageTabData((prev) => ({
            ...prev,
            yearlyUsage: { ...prev.yearlyUsage, data: next },
        }));
        handleYearlyUsageChange(next);
    }, [open, electricBillMonthForEffect, usageTabData?.yearlyUsage?.data]);

    const handleDailyUsageChange = (dailyUsage: any) => {
        const annual = Number(dailyUsage.AnnualUsage) || 0;
        const data = {
            AnnualUsage: annual,
            avgDailyUsage: annual > 0 ? (annual / 365).toFixed(2) : '',
            ElectricBillMonth: electricBillMonthFromYearlyTabData(usageTabData.yearlyUsage?.data),
        };

        let updatedAnnualUsageTabContent = {
            ...usageTabData,
            yearlyUsage: { key: 3, type: 'Yearly', data: data },
        };

        setUsageTabData(updatedAnnualUsageTabContent as UsageData);
        handleYearlyUsageChange(data);

        let dailyUsageJson = {
            AnnualUsage: dailyUsage.AnnualUsage,
            Data: dailyUsage.Data,
        };
        setDailyUsageJson(dailyUsageJson);
        markUtilityDirty();
    };

    const handleMonthlyUsageChange = (monthlyUsage: any) => {
        const annual = Number(monthlyUsage.AnnualUsage) || 0;
        const data = {
            AnnualUsage: annual,
            avgDailyUsage: annual > 0 ? (annual / 365).toFixed(2) : '',
            ElectricBillMonth: electricBillMonthFromYearlyTabData(usageTabData.yearlyUsage?.data),
        };
        let updatedAnnualUsageTabContent = {
            ...usageTabData,
            monthlyUsage: { ...usageTabData.monthlyUsage, data: monthlyUsage },
            yearlyUsage: { key: 3, type: 'Yearly', data: data },
        };

        setUsageTabData(updatedAnnualUsageTabContent as UsageData);
        setMonthlyUsage(monthlyUsage);
        handleYearlyUsageChange(data);
        markUtilityDirty();
    };

    const updateCheckboxFieldValue = (field: any, event: any) => {
        setUtilityCompanyFieldList((prev) => prev.map((ele) => (ele.field === field.field ? { ...ele, value: event.target.checked } : ele)));
        markUtilityDirty();
    };

    const removeUploadFile = (index: number) => {
        ClearBillFields();
        setUploadFiles?.([]);
        setIsFileChange(false);
        markUtilityDirty();
    };

    const utlityCompanyFieldUpdated = (value: any, field: any) => {
        switch (field.field) {
            case 'selectCompany':
                updateValue('utilityCompanyFieldList', utilityCompanyFieldList, field, value);
                const companyName = companyList.find((ele: any) => ele.value === value);
                utlityCompanyUpdated(companyName)
                break;
            case 'selectCurrentRate':
            case 'selectProposedRates':
            case 'selectUtilityIncrease':
            case 'currentAllInUtilityRate':
                updateValue('utilityCompanyFieldList', utilityCompanyFieldList, field, value);
                break;
            case 'selectProposedDemand':
                updateValue('utilityCompanyFieldList', utilityCompanyFieldList, field, value);
                break;
            case 'selectCurrentDemand':
                updateValue('utilityCompanyFieldList', utilityCompanyFieldList, field, value);
                break;
        }
        markUtilityDirty();
    };

    const updateValue = (listName: string, list: any, field: any, value: any) => {
        setUtilityCompanyFieldList((prev) => prev.map((ele: any) => (ele.field == field.field ? { ...ele, value } : ele)));

    }

    /** Angular `utility-info` / Swal parity — Lightreach change-order confirmation before persisting utility. */
    const showChangeOrderConfirmationDialog = () =>
        new Promise<boolean>((resolve) => {
            changeOrderConfirmResolveRef.current = resolve;
            setChangeOrderConfirmOpen(true);
        });

    /** On confirm: Trinity `utility-info` `lightReachVoidQuote` (Design API), then resolve so save continues. */
    const finishChangeOrderConfirm = async (confirmed: boolean) => {
        const r = changeOrderConfirmResolveRef.current;
        changeOrderConfirmResolveRef.current = null;

        const settle = (value: boolean) => {
            setChangeOrderConfirmOpen(false);
            r?.(value);
        };

        const haveLightreachProduct =
            opportunityDetails?.OpportunityProduct?.some(
                (product: any) => product.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName,
            )

        const oppId = opportunityID ?? opportunityDetails?.Opportunity_Id;
        if (haveLightreachProduct && oppId != null && confirmed) {
            try {
                const products = opportunityDetails?.OpportunityProduct ?? [];
                const batteryLease = products.find(
                    (product: any) =>
                        product.Product_Type_Name === 'Battery' &&
                        product.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName &&
                        String(product.Finance_Method_Name ?? '').toLowerCase().includes('battery lease'),
                );
                const lightreachLeads = listSyncedIgsAndLightreachLeads(opportunityDetails).filter(
                    (row) => row.partner === 'Lightreach' && !row.productType.toLowerCase().includes('roof'),
                );
                // V1 lightReachVoidQuote: Battery Lease on a multi-product deal voids each Lightreach lead, not only Solar.
                if (products.length > 1 && batteryLease && lightreachLeads.length > 0) {
                    for (const lead of lightreachLeads) {
                        await designServices.lightReachVoidQuote(oppId, lead.leadId);
                    }
                } else if (opportunityDetails?.Finance_Partner_Sync_LeadID) {
                    await designServices.lightReachVoidQuote(oppId, opportunityDetails.Finance_Partner_Sync_LeadID);
                }
            } catch (e: any) {
                const data = e?.response?.data;
                let text = data?.title || 'Could not void Lightreach quote.';
                if (data?.errors && typeof data.errors === 'object') {
                    const parts = Object.values(data.errors).flat();
                    if (parts.length > 0) text = Array.isArray(parts[0]) ? (parts[0] as string[])[0] : String(parts[0]);
                }
                messageApi.error(text);
                settle(false);
                return;
            }
        }

        settle(true);
    };

    /**
     * Trinity `utility-info` `checkChangeOrder` (ported from Angular component + `utility-info.service` loan rows).
     * Fetches `getLoanStatus`, derives `isUtilityUpdatedFlag`, builds labeled diffs, optionally calls `UpdateLoanStatus` + checklist.
     */
    const checkChangeOrderForSave = async (params: {
        opportunityDetailsArg: any;
        utilityCompanyFieldListArg: any[];
        newCurrentValArrayArg: { value: string; label: string }[];
        newProposedValArrayArg: { value: string; label: string }[];
        companyListArg: { value: string; label: string }[];
        initialSelects: { selectCompany: string; selectCurrentRate: string; selectProposedRates: string };
        initialCurrOpts: { value: string; label: string }[];
        initialPropOpts: { value: string; label: string }[];
        solarUtilityDataClone: any;
        preSaveSolarUtility: any;
        annualUsageNew: number;
        numericOppId: string | number;
    }): Promise<UtilityChangeOrderCheckResult> => {
        const {
            opportunityDetailsArg,
            utilityCompanyFieldListArg,
            newCurrentValArrayArg,
            newProposedValArrayArg,
            companyListArg,
            initialSelects,
            initialCurrOpts,
            initialPropOpts,
            solarUtilityDataClone,
            preSaveSolarUtility,
            annualUsageNew,
            numericOppId,
        } = params;

        const op = opportunityDetailsArg?.opportunityProduct ?? opportunityDetailsArg?.OpportunityProduct;
        const hasLightReachSolarOrBatteryFinancePartner = Array.isArray(op)
            ? op.some(
                  (product: any) =>
                      isSameFinancePartner(getFinancePartnerName(product), COMMON_CONSTANTS.LightreachPartnerName) &&
                      isSolarOrBatteryFinanceProduct(product),
              )
            : false;
        const hasGoodLeapSolarFinancePartner = Array.isArray(op)
            ? op.some(
                  (product: any) =>
                      product.Product_Type_Name === COMMON_CONSTANTS.SolarProductName &&
                      product.Finance_Partner === COMMON_CONSTANTS.GoodLeapPartnerName,
              )
            : false;

        let changeOrderDetails: any[] = [];
        try {
            const raw = await quoteServices.contractGetLoanStatusByOpportunity(numericOppId);
            changeOrderDetails = Array.isArray(raw) ? raw : raw != null && typeof raw === 'object' ? [raw] : [];
        } catch {
            changeOrderDetails = [];
        }

        if (Array.isArray(changeOrderDetails)) {
            const goodLeapData = changeOrderDetails.find(
                (x: any) => x.Finance_Partner_Name === COMMON_CONSTANTS.GoodLeapPartnerName,
            );
            const lightReachData = changeOrderDetails.find(
                (x: any) => x.Finance_Partner_Name === COMMON_CONSTANTS.LightreachPartnerName,
            );
            if (goodLeapData && hasGoodLeapSolarFinancePartner) {
                isUtilityUpdatedFlagRef.current = !!goodLeapData.Is_Change_Order;
            }
            if (lightReachData && hasLightReachSolarOrBatteryFinancePartner) {
                isUtilityUpdatedFlagRef.current =
                    changeOrderDetails.length > 0 && lightReachData ? !!lightReachData.Is_Utility_Updated : false;
            }
        }

        const lightreachLoanRows = changeOrderDetails.filter(
            (x: any) =>
                String(x?.Finance_Partner_Name ?? '').toLowerCase() ===
                COMMON_CONSTANTS.LightreachPartnerName.toLowerCase(),
        );
        const contractStatusLocked = lightreachLoanRows.some((x: any) => {
            const status = String(x?.application_Status ?? '').trim();
            return status === 'Signed' || status === 'Sent' || status === 'Generated';
        });

        const getVal = (field: string) => {
            const v = getUtilityInformation(utilityCompanyFieldListArg, field);
            return v == null || v === '' ? '' : String(v);
        };
        const utilityFormValue = {
            selectCompany: getVal('selectCompany'),
            selectCurrentRate: getVal('selectCurrentRate'),
            selectProposedRates: getVal('selectProposedRates'),
        };

        const utilityNew = {
            ...utilityFormValue,
            selectCompany: getLabelByIdForChangeOrder([companyListArg], utilityFormValue.selectCompany),
            selectCurrentRate: getLabelByIdForChangeOrder(
                [newCurrentValArrayArg, initialCurrOpts],
                utilityFormValue.selectCurrentRate,
            ),
            selectProposedRates: getLabelByIdForChangeOrder(
                [newProposedValArrayArg, initialPropOpts],
                utilityFormValue.selectProposedRates,
            ),
        };

        const utilityOld = {
            ...initialSelects,
            selectCompany: getLabelByIdForChangeOrder([companyListArg], initialSelects.selectCompany),
            selectCurrentRate: getLabelByIdForChangeOrder(
                [initialCurrOpts, newCurrentValArrayArg],
                initialSelects.selectCurrentRate,
            ),
            selectProposedRates: getLabelByIdForChangeOrder(
                [initialPropOpts, newProposedValArrayArg],
                initialSelects.selectProposedRates,
            ),
        };

        const utilityFieldLabels: Record<string, string> = {
            selectCompany: 'Utility Company',
            selectProposedRates: 'Proposed Rate Plan',
            selectCurrentRate: 'Current Rate Plan',
        };

        const utilityChanges: string[] = [];
        for (const key of Object.keys(utilityFieldLabels)) {
            const nk = (utilityNew as any)[key] ?? '';
            const ok = (utilityOld as any)[key] ?? '';
            if (nk !== ok) {
                utilityChanges.push(`${utilityFieldLabels[key]} changed from '${ok}' to '${nk}'`);
            }
        }

        const annualUsageOld =
            solarUtilityDataClone?.SolarUtility?.AnnualUsage ?? preSaveSolarUtility?.AnnualUsage ?? '';
        if (String(annualUsageOld) !== String(annualUsageNew)) {
            utilityChanges.push(`Annual Usage changed from '${annualUsageOld}' to '${annualUsageNew}'`);
        }

        const utilityCompanyChanged = initialSelects.selectCompany !== utilityFormValue.selectCompany;
        const utilityCurrentRatePlanChanged = initialSelects.selectCurrentRate !== utilityFormValue.selectCurrentRate;
        const utilityInstallRatePlanChanged =
            initialSelects.selectProposedRates !== utilityFormValue.selectProposedRates;

        const isUtilityFormChanged = utilityChanges.length > 0;


        const requiresConfirmation =
            (hasLightReachSolarOrBatteryFinancePartner && contractStatusLocked) ||
            (hasGoodLeapSolarFinancePartner && isUtilityUpdatedFlagRef.current && isUtilityFormChanged);


        if (requiresConfirmation) {
            if (utilityCompanyChanged || utilityCurrentRatePlanChanged || utilityInstallRatePlanChanged) {
                try {
                    await quoteServices.contractUpdateLoanStatus(numericOppId, 'Utility', 0);
                } catch {
                    /* Angular nested subscribe: swallow */
                }
                try {

                    await quoteServices.savePageChanges({
                        opportunityId: numericOppId,
                        pageName: 'Utility',
                        changes: utilityChanges.join(' '),
                    });
                    await quoteServices.getPageChanges(numericOppId);
                } catch {
                    /* Angular: no toast */
                }
            }
            return {
                requiresConfirmation: true,
                utilityCompanyChanged,
                utilityChanges,
                utilityCurrentRatePlanChanged,
                utilityInstallRatePlanChanged,
            };
        }

        return {
            requiresConfirmation: false,
            utilityCompanyChanged: false,
            utilityChanges: [],
            utilityCurrentRatePlanChanged: false,
            utilityInstallRatePlanChanged: false,
        };
    };

    const utilityApiErrorMessage = (error: any, fallback: string) => {
        const data = error?.response?.data;
        let text = (typeof data?.title === 'string' && data.title) || fallback;
        if (data?.errors && typeof data.errors === 'object') {
            const parts = Object.values(data.errors).flat();
            if (parts.length > 0) {
                text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
            }
        }
        return text;
    };

    const saveUtilityDetails = async () => {
        setSaveLoading(true);
        setSaveResponse(null);
        const existingSolarUtility =
            utilityInformation?.SolarUtility ?? solarUtilityData?.SolarUtility ?? {};
        let temputilityInformation = {
            ...(utilityInformation && !Array.isArray(utilityInformation) ? utilityInformation : {}),
            SolarUtility: existingSolarUtility,
        };
        const preSaveSolarUtility = existingSolarUtility;
        try {


            let proposedRate = newCurrentValArray.filter((x: any) => String(x.value) === String(getUtilityInformation(utilityCompanyFieldList, 'selectProposedRates')));
            let currentRate = newProposedValArray.filter((x: any) => String(x.value) === String(getUtilityInformation(utilityCompanyFieldList, 'selectCurrentRate')));

            const utilityFinanceProducts =
                utilityInformation?.OpportunityProductsFinanceInfo?.length > 0 ? utilityInformation?.OpportunityProductsFinanceInfo:
                (opportunityDetails?.OpportunityProduct?.length > 0 ? opportunityDetails?.OpportunityProduct :
                []);

            const haveCurrentAllInUtilityRate =
                hasUtilityFinancePartner(utilityFinanceProducts, COMMON_CONSTANTS.IGSPartnerName) ||
                hasUtilityFinancePartner(utilityFinanceProducts, COMMON_CONSTANTS.GoodLeapPartnerName) ||
                hasUtilityFinancePartner(utilityFinanceProducts, COMMON_CONSTANTS.LightreachPartnerName);

            let utilityIncreaseValue = typeof getUtilityInformation(utilityCompanyFieldList, 'selectUtilityIncrease') == 'object' ?
                getUtilityInformation(utilityCompanyFieldList, 'selectUtilityIncrease')?.Value :
                getUtilityInformation(utilityCompanyFieldList, 'selectUtilityIncrease');
            let EnterAnnualUsage = '';
            let AnnualUsage = 0;
            if (tabIndex == '1') {
                EnterAnnualUsage = "DailyUsageCont";
                AnnualUsage = Number(dailyUsageJson?.AnnualUsage) || 0;
            } else if (tabIndex == '2') {
                EnterAnnualUsage = "MonthlyUsageCont";
                const src = getMonthlyUsageDataSource();
                AnnualUsage = src?.Data?.length
                    ? annualKwhFromMonthlyUsageData(src.Data)
                    : Number(monthlyUsage?.AnnualUsage ?? usageTabData?.monthlyUsage?.data?.AnnualUsage) || 0;
            } else {
                EnterAnnualUsage = "YearlyUsageCont";
                const yearlyAnnual = yearlyUsage?.AnnualUsage ?? usageTabData?.yearlyUsage?.data?.AnnualUsage ?? (typeof usageTabData?.yearlyUsage?.data === 'object' ? (usageTabData.yearlyUsage.data as any)?.AnnualUsage : undefined);
                AnnualUsage = Number(yearlyAnnual) || 0;
            }
            const companyInvalid = validateutilityCompanyFieldList();
            const billInvalid = validateBillDetailFieldList();
            const usageInvalid =
                !Number.isFinite(AnnualUsage) || AnnualUsage <= 0;
            if (usageInvalid || companyInvalid || billInvalid) {
                setSaveResponse({
                    type: 'error',
                    message: UTILITY_SAVE_VALIDATION_SUMMARY,
                });
                setSaveLoading(false);
                return;
            }

            const haveLightreachProduct = opportunityDetails?.OpportunityProduct?.some(
                (product: any) => product.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName,
            );

            const numericOppId = opportunityID ?? opportunityDetails?.Opportunity_Id;
            const data = JSON.parse(JSON.stringify(solarUtilityData ?? {}));

            const changeOrderBaseline = changeOrderInitialSelectsRef.current;

            if (changeOrderBaseline != null && numericOppId != null) {
                const {
                    requiresConfirmation,
                    utilityCompanyChanged,
                    utilityCurrentRatePlanChanged,
                    utilityInstallRatePlanChanged,
                } = await checkChangeOrderForSave({
                    opportunityDetailsArg: opportunityDetails,
                    utilityCompanyFieldListArg: utilityCompanyFieldList,
                    newCurrentValArrayArg: newCurrentValArray,
                    newProposedValArrayArg: newProposedValArray,
                    companyListArg: companyList,
                    initialSelects: changeOrderBaseline,
                    initialCurrOpts: initialCurrentValArrayRef.current,
                    initialPropOpts: initialProposedValArrayRef.current,
                    solarUtilityDataClone: data,
                    preSaveSolarUtility,
                    annualUsageNew: AnnualUsage,
                    numericOppId,
                });

                let forceSyncOverride = false;
                const pageRedirection = false;

                if (
                    (requiresConfirmation &&
                        (utilityCompanyChanged || utilityCurrentRatePlanChanged || utilityInstallRatePlanChanged) &&
                        !hasShownConfirmationRef.current) ||
                    (isUtilityUpdatedFlagRef.current && !hasShownConfirmationRef.current)
                ) {
                    hasShownConfirmationRef.current = true;
                    if (haveLightreachProduct) {
                        forceSyncOverride = await showChangeOrderConfirmationDialog();
                    }
                    if (!forceSyncOverride) {
                        isUtilityUpdatedFlagRef.current = false;
                        if (haveLightreachProduct) {
                            try {
                                await quoteServices.contractUpdateLoanStatus(numericOppId, 'Utility', 0);
                            } catch {
                                /* Angular: swallow */
                            }
                        }
                    } else {
                        try {
                            await quoteServices.contractGetLoanStatusByOpportunity(numericOppId);
                        } catch {
                            /* Angular: swallow */
                        }
                        setQuotesRefreshTrigger(Date.now());
                    }
                    if (!haveLightreachProduct) {
                        try {
                            await quoteServices.contractUpdateLoanStatus(numericOppId, 'Utility', 0);
                        } catch {
                            /* Angular: swallow */
                        }
                    }
                    if (!pageRedirection) {
                        hasShownConfirmationRef.current = false;
                    }
                    if (haveLightreachProduct && !forceSyncOverride) {
                        setSaveLoading(false);
                        return;
                    }
                }
            }

            let fileDetails = {
                FileName: '',
                FilePath: '',
            };

            const fileToUpload = uploadFiles?.[0];
            if (isFileChange && fileToUpload && fileToUpload.size > 0) {
                setIsUploading(true);
                setUploadProgress(0);
                setSaveResponse({ type: 'info', message: 'Uploading utility bill (0%)...' });
                try {
                    fileDetails = await designServices.UploadUtilityFile(
                        fileToUpload,
                        opportunityID ?? opportunityDetails?.Opportunity_Id,
                        (progress: number) => {
                            setUploadProgress(progress);
                            setSaveResponse({ type: 'info', message: `Uploading utility bill (${progress}%)...` });
                        },
                    );
                    setUploadProgress(100);
                    setSaveResponse({ type: 'info', message: 'Uploading utility bill (100%) completed...' });
                } catch (uploadError: any) {
                    const text = utilityApiErrorMessage(uploadError, 'Failed to upload utility bill.');
                    setSaveResponse({ type: 'error', message: text });
                    messageApi.error(text);
                    return;
                } finally {
                    setIsUploading(false);
                }
            }

            let solarUtility: any = {
                SolarUtilityId: temputilityInformation.SolarUtility.SolarUtilityId,
                SolarSystemDesignId: temputilityInformation.SolarUtility.SolarSystemDesignId,
                UtilityOwnerName: temputilityInformation.SolarUtility.UtilityOwnerName,
                StateId: temputilityInformation.SolarUtility.StateId,

                UtilityCompany: (() => { const v = getUtilityInformation(utilityCompanyFieldList, 'selectCompany'); const n = Number(v); return (v !== '' && v != null && !Number.isNaN(n)) ? n : (v ?? ''); })(),
                GenabilityId: temputilityInformation.SolarUtility.GenabilityId,
                CurrentRatePlan: (currentRate.length > 0) ? (currentRate[0].label ?? currentRate[0].value) : (temputilityInformation.SolarUtility.CurrentRatePlan) ? temputilityInformation.SolarUtility.CurrentRatePlan : '',
                CurrentRatePlanId: (currentRate.length > 0) ? Number(currentRate[0].value) : (temputilityInformation.SolarUtility.CurrentRatePlanId) ? temputilityInformation.SolarUtility.CurrentRatePlanId : 0,
                currentAllInUtilityRate: (haveCurrentAllInUtilityRate ? getUtilityInformation(utilityCompanyFieldList, 'currentAllInUtilityRate') : 0),
                CustomerCharge: (getUtilityInformation(utilityCompanyFieldList, 'customerCharge') == '') ? 0 : getUtilityInformation(utilityCompanyFieldList, 'customerCharge'),
                PostInstallRatePlanId: (proposedRate.length > 0) ? Number(proposedRate[0].value) : (temputilityInformation.SolarUtility.PostInstallRatePlanId) ? temputilityInformation.SolarUtility.PostInstallRatePlanId : 0,
                PostInstallRatePlan: (proposedRate.length > 0) ? (proposedRate[0].label ?? proposedRate[0].value) : (temputilityInformation.SolarUtility.PostInstallRatePlan) ? temputilityInformation.SolarUtility.PostInstallRatePlan : '',
                OutStandingBalance: (getUtilityInformation(utilityCompanyFieldList, 'outstandingBalance') == '') ? 0 : getUtilityInformation(utilityCompanyFieldList, 'outstandingBalance'),
                EstAnnualUtilityIncrease: Number(utilityIncreaseValue),
                EnterAnnualUsage: EnterAnnualUsage,
                FileName: fileDetails.FileName || temputilityInformation.SolarUtility.FileName || null,
                FilePath: fileDetails.FilePath || temputilityInformation.SolarUtility.FilePath || null,
                DocumentId: temputilityInformation.SolarUtility.DocumentId,
                AnnualUsage: AnnualUsage,
                // CurrentAmountDue: (annualUsageForm.value['amountDue'] == '') ? 0 : annualUsageForm.value['amountDue'],
                ElectricBillMonth: yearlyUsage?.ElectricBillMonth ?? temputilityInformation.SolarUtility?.ElectricBillMonth ?? '',
                MonthKWUsage: temputilityInformation.SolarUtility.MonthKWUsage,
                MonthAmount: temputilityInformation.SolarUtility.MonthAmount,
                MonthKWUsageTotal: temputilityInformation.totalAmount,
                MonthAmountTotal: JSON.stringify(temputilityInformation.totalAmount),
                ShutoffNotice: getUtilityInformation(billDetailFieldList, 'shutoffNotice') ?? false,
                UtilityLIDRate: getUtilityInformation(utilityCompanyFieldList, 'utilityLIDRate'),
                Utility_Rate: getUtilityInformation(billDetailFieldList, 'utilityRate') ?? null,
                January: tabIndex == '1' ? (Number(getMonthlyKWUsage('January')) || 0) : getMonthlyUsage('Jan'),
                JanuaryMonthly: getMonthlyAmount('Jan'),
                February: tabIndex == '1' ? (Number(getMonthlyKWUsage('February')) || 0) : getMonthlyUsage('Feb'),
                FebruaryMonthly: getMonthlyAmount('Feb'),
                March: tabIndex == '1' ? (Number(getMonthlyKWUsage('March')) || 0) : getMonthlyUsage('Mar'),
                MarchMonthly: getMonthlyAmount('Mar'),
                April: tabIndex == '1' ? (Number(getMonthlyKWUsage('April')) || 0) : getMonthlyUsage('Apr'),
                AprilMonthly: getMonthlyAmount('Apr'),
                May: tabIndex == '1' ? (Number(getMonthlyKWUsage('May')) || 0) : getMonthlyUsage('May'),
                MayMonthly: getMonthlyAmount('May'),
                June: tabIndex == '1' ? (Number(getMonthlyKWUsage('June')) || 0) : getMonthlyUsage('Jun'),
                JuneMonthly: getMonthlyAmount('Jun'),
                July: tabIndex == '1' ? (Number(getMonthlyKWUsage('July')) || 0) : getMonthlyUsage('Jul'),
                JulyMonthly: getMonthlyAmount('Jul'),
                August: tabIndex == '1' ? (Number(getMonthlyKWUsage('August')) || 0) : getMonthlyUsage('Aug'),
                AugustMonthly: getMonthlyAmount('Aug'),
                September: tabIndex == '1' ? (Number(getMonthlyKWUsage('September')) || 0) : getMonthlyUsage('Sep'),
                SeptemberMonthly: getMonthlyAmount('Sep'),
                October: tabIndex == '1' ? (Number(getMonthlyKWUsage('October')) || 0) : getMonthlyUsage('Oct'),
                OctoberMonthly: getMonthlyAmount('Oct'),
                November: tabIndex == '1' ? (Number(getMonthlyKWUsage('November')) || 0) : getMonthlyUsage('Nov'),
                NovemberMonthly: getMonthlyAmount('Nov'),
                December: tabIndex == '1' ? (Number(getMonthlyKWUsage('December')) || 0) : getMonthlyUsage('Dec'),
                DecemberMonthly: getMonthlyAmount('Dec'),
                UploadUtilityBill: null,
                SolarLoadCalcTotal: temputilityInformation.SolarUtility.SolarLoadCalcTotal,
                BatteryLoadCalcTotal: temputilityInformation.SolarUtility.BatteryLoadCalcTotal,
                DailyUsage: dailyUsageJson
            };

            let SolarMeter: any[] = [
                {
                    AccountNumber: getUtilityInformation(billDetailFieldList, 'accountNumber'),
                    BillDate: toApiBillDate(getUtilityInformation(billDetailFieldList, 'dateofBill')),
                    MeterId: getUtilityInformation(billDetailFieldList, 'meterId'),
                    UtilityMeterId: getUtilityInformation(billDetailFieldList, 'utilityMeterId')
                }
            ]

            temputilityInformation.SolarUtility = solarUtility;
            temputilityInformation.SolarMeter = SolarMeter;
            await designServices.SaveProductConfigUtility(temputilityInformation);
            const partnerProducts = (opportunityDetails?.OpportunityProduct ?? []).filter((x: any) =>
                (x.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName ||
                    x.Finance_Partner === COMMON_CONSTANTS.IGSPartnerName) &&
                x.ActiveFlag !== false,
            );
            if (partnerProducts.length > 0) {
                const batteryIsLightreach = partnerProducts.some(
                    (x: any) =>
                        x.Product_Type_Name === 'Battery' &&
                        x.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName,
                );
                const order = ['Solar', 'Battery', 'Battery Only', 'Roof', 'Roof Only'];
                // V1 check-list-menu: Battery Lease Lightreach syncs every open lead (Solar and Battery Only), not Solar alone.
                let productsToSync = batteryIsLightreach
                    ? partnerProducts.filter((x: any) => x.Product_Type_Name !== 'Roof' && x.Product_Type_Name !== 'RR' && x.Product_Type_Name !== 'RRR')
                    : [
                          partnerProducts.find((x: any) => x.Product_Type_Name === 'Solar') ??
                              partnerProducts.find((x: any) => x.Product_Type_Name === 'Battery'),
                      ].filter(Boolean);
                // Sort the array in place
                productsToSync.sort((a:any, b:any) => {
                    const indexA = order.indexOf(a.Product_Type_Name);
                    const indexB = order.indexOf(b.Product_Type_Name);
                    // Handle items missing from customOrder (move them to the end)
                    const posA = indexA === -1 ? Infinity : indexA;
                    const posB = indexB === -1 ? Infinity : indexB;
                    return posA - posB;
                });
                const financeMethodCounts = productsToSync.reduce((acc: Record<string, number>, product: any) => {
                    const methodName = String(product.Finance_Method_Name ?? '');
                    acc[methodName] = (acc[methodName] ?? 0) + 1;
                    return acc;
                }, {});
                productsToSync = productsToSync.filter((product: any) => {
                    const methodName = String(product.Finance_Method_Name ?? '');
                    if ((financeMethodCounts[methodName] ?? 0) <= 1) return true;
                    return String(product.Product_Type_Name ?? '') === 'Solar';
                });

                for (const product of productsToSync) {
                    const productType =
                        product.Product_Type_Name === 'Battery' ? 'Battery Only' : product.Product_Type_Name;
                    if (!productType || opportunityDetails?.Opportunity_Id == null) continue;
                    await syncUtilityWithRetries({
                        opportunityId: String(opportunityDetails.Opportunity_Id),
                        productType,
                        partnerName: String(product.Finance_Partner ?? ''),
                    });
                }
                if (opportunityDetails?.Opportunity_Id != null) {
                    const stats = await quoteServices.getSyncStatusList(String(opportunityDetails.Opportunity_Id));
                    const list = stats?.FinancePartnerSyncStatuses || (Array.isArray(stats) ? stats : []);
                    if (Array.isArray(list)) {
                        useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(list);
                    }
                }
            }
            const refreshed = numericOppId != null ? await designServices.RetrieveUtilityInformation(numericOppId) : null;
            if (refreshed) {
                setUtilityInformation(refreshed.utilityDetails);
                setSolarUtilityData(refreshed.utilityDetails);
                const apiCompanyRaw = refreshed.utilityDetails?.SolarUtility?.UtilityCompany;
                const apiStr = String(apiCompanyRaw ?? '').trim();
                const companyMatchByLabel = companyList.find((c: any) => String(c.label ?? '').trim() === apiStr);
                const companyMatchByValue = companyList.find((c: any) => String(c.value ?? '') === apiStr);
                const companyMatch = companyMatchByLabel || companyMatchByValue;
                console.log('Utility Company mapped', {
                    id: companyMatch?.value ?? null,
                    name: companyMatch?.label ?? null,
                    raw: apiStr,
                });
                setUtilityCompanyName(companyMatch ? companyMatch.label : null);
                setPricingNetworkRefreshTrigger(Date.now());
                setUtilityCompanyFieldList(buildUtilityCompanyFieldList(
                    refreshed.utilityDetails,
                    getUtilityCompanyFieldConfig([
                        ...(refreshed.utilityDetails?.OpportunityProductsFinanceInfo || []),
                        ...(opportunityDetails?.OpportunityProduct || opportunityDetails?.opportunityProduct || []),
                    ])
                ));
                setBillDetailFieldList(buildBillDetailFieldList(refreshed.utilityDetails));
                const rebuiltUsage = buildUsageAndBillFromUtilityDetails(refreshed.utilityDetails);
                setUsageTabData(rebuiltUsage.usageData);
                setMonthlyUsage(rebuiltUsage.usageData.monthlyUsage.data);
                handleYearlyUsageChange(rebuiltUsage.usageData.yearlyUsage.data);
                const getTabIdx = (annualUsageTypeValue: string) => {
                    switch (annualUsageTypeValue) {
                        case 'DailyUsageCont': return '1';
                        case 'MonthlyUsageCont': return '2';
                        case 'YearlyUsageCont': return '3';
                        default: return '1';
                    }
                };
                setTabIndex(getTabIdx(refreshed.annualUsageType));

                if (numericOppId != null) {
                    try {
                        const priceQuote = await quoteServices.getRetrievePriceQuote(String(numericOppId));
                        if (priceQuote) {
                            setPriceQuoteDetails(priceQuote as any);
                        }
                    } catch (err) {
                        console.error('Failed to retrieve price quote after utility save', err);
                    }
                }

                setSaveResponse({ type: 'success', message: 'Utility details saved successfully.' });
                setUtilityDetailsDirty(false);
                setIsFileChange(false);
                changeOrderBaselineCapturedRef.current = false;
            } else {
                setSaveResponse({ type: 'success', message: 'Utility details saved.' });
                setUtilityDetailsDirty(false);
                setIsFileChange(false);
                changeOrderBaselineCapturedRef.current = false;
            }

            if (
                numericOppId != null &&
                shouldRunUpdateLoanStatusAfterUtilitySave(solarUtility, preSaveSolarUtility, opportunityDetails)
            ) {
                await runUtilitySaveUpdateLoanStatusFlow(
                    numericOppId,
                    opportunityDetails,
                    setQuotesRefreshTrigger,
                    () =>
                        messageApi.warning(
                            'New quote is required due to changes in utility information before change order'
                        )
                );
            }
        } catch (error: any) {
            console.error('Failed to save utility details', error);
            const text = utilityApiErrorMessage(error, 'Failed to save utility details.');
            setSaveResponse({ type: 'error', message: text });
            messageApi.error(text);
        } finally {
            setSaveLoading(false);
            setIsUploading(false);
        }

    }


    const getUtilityInformation = (list: any[], field: string) => {
        const value = list.find((item: any) => item.field === field)?.value;
        return value == '' ? null : value;
    }

    /** Converts UI date (e.g. MM-DD-YYYY) to API-safe ISO date (YYYY-MM-DD) or null for DateTime? */
    const toApiBillDate = (value: string | null | undefined): string | null => {
        if (value == null || String(value).trim() === '') return null;
        const d = dayjs(value, ['YYYY-MM-DD', 'MM-DD-YYYY'], true);
        return d.isValid() ? d.format('YYYY-MM-DD') : null;
    };

    /** Monthly grid data: state is often null until first change; always fall back to usageTabData so save includes edits. */
    const getMonthlyUsageDataSource = () => {
        const fromState = monthlyUsage?.Data?.length ? monthlyUsage : null;
        const fromTabs = usageTabData?.monthlyUsage?.data?.Data?.length ? usageTabData.monthlyUsage.data : null;
        return fromState ?? fromTabs;
    };

    const findMonthlyRow = (shortMonth: string, data: any[]) => {
        const s = String(shortMonth ?? '').trim().toLowerCase();
        return data.find((item: any) => {
            const m = String(item?.Month ?? '').trim();
            const ml = m.toLowerCase();
            return ml === s || ml.slice(0, 3) === s.slice(0, 3);
        });
    };

    const getMonthlyUsage = (Month: string) => {
        const src = getMonthlyUsageDataSource();
        if (!src?.Data?.length) return 0;
        const row = findMonthlyRow(Month, src.Data);
        const v = row?.MonthUsage;
        if (v === '' || v == null) return 0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const getMonthlyAmount = (Month: string) => {
        const src = getMonthlyUsageDataSource();
        if (!src?.Data?.length) return 0;
        const row = findMonthlyRow(Month, src.Data);
        const v = row?.Amount ?? row?.MonthAmount;
        if (v === '' || v == null) return 0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    }

    const getMonthlyKWUsage = (Month: string) => {
        return dailyUsageJson?.Data?.find((item: any) => item.Month === Month)?.MonthKWUsage;
    }

    const validateutilityCompanyFieldList = () => {
        let invalid = false;
        utilityCompanyFieldList.forEach((field: any) => {
            if (field.hidden) return;
            const isRequired = !!(field.require || field.required);
            if (!isRequired) return;
            if (field.dataType === 'Checkbox') return;
            const v = field.value;
            const text = v == null ? '' : String(v).trim();
            if (
                text === '' ||
                text === '0' ||
                text.toUpperCase() === 'N/A'
            ) {
                invalid = true;
                return;
            }
            // Match Trinity: All In rate must be > 0 (Validators.min(0.001)).
            if (field.field === 'currentAllInUtilityRate') {
                const n = Number(text);
                if (!Number.isFinite(n) || n < 0.001) {
                    invalid = true;
                }
            }
        });
        return invalid;
    };

    const validateBillDetailFieldList = () => {
        let invalid = false;
        billDetailFieldList.forEach((field: any) => {
            if (field.hidden) return;
            const isRequired = !!(field.require || field.required);
            if (!isRequired) return;
            const v = field.value;
            if (field.dataType === 'radioButton' || field.dataType === 'Checkbox') {
                if (v == null || v === '') invalid = true;
                return;
            }
            if (v == null || String(v).trim() === '') {
                invalid = true;
            }
        });
        return invalid;
    };


    useEffect(() => {
        if (!open) return;
        setUtilityPanelError(null);
        setLoader(true);
        const numericOppId = opportunityID ?? opportunityDetails?.Opportunity_Id;
        if (!numericOppId || !opportunityDetails) {
            setUtilityPanelError(
                !numericOppId
                    ? 'Opportunity ID is missing. Open this design from an opportunity to edit utility details.'
                    : 'Opportunity details are still loading. Close and reopen in a moment.',
            );
            setLoader(false);
            return;
        }
        const opportunityProducts = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const financePartner = opportunityProducts.find((p: any) => p.Product_Type_Name === 'Solar' || p.Product_Type_Name === 'Battery');
        const installAddr = opportunityDetails?.InstallationAddress;
        const stateCode = String(
            installAddr?.stateCode ?? installAddr?.State ?? installAddr?.state ?? '',
        ).trim();
        if (!financePartner?.Finance_Partner_Id || !stateCode) {
            setUtilityPanelError(
                !financePartner?.Finance_Partner_Id
                    ? 'No Solar or Battery product / finance partner found on this opportunity.'
                    : 'Installation address is missing a state code, so utility companies cannot be loaded.',
            );
            setLoader(false);
            return;
        }
        const opportunityFieldConfig = getUtilityCompanyFieldConfig(opportunityProducts);
        setUtilityCompanyFieldList(opportunityFieldConfig);


        const applyUtilityDetails = (utilityDetails: any, companyList: { value: string; label: string }[]) => {
            setUtilityInformation(utilityDetails);
            const fieldConfig = getUtilityCompanyFieldConfig(
                [
                    ...(utilityDetails?.OpportunityProductsFinanceInfo || []),
                    ...opportunityProducts,
                ]
            );
            const built = buildUtilityCompanyFieldList(utilityDetails, fieldConfig);
            const apiCompanyRaw = utilityDetails?.SolarUtility?.UtilityCompany;
            const apiStr = String(apiCompanyRaw ?? '').trim();
            const companyMatchByLabel = companyList.find((c: any) => String(c.label ?? '').trim() === apiStr);
            const companyMatchByValue = companyList.find((c: any) => String(c.value ?? '') === apiStr);
            const companyMatch = companyMatchByLabel || companyMatchByValue;
            console.log('Utility Company mapped', {
                id: companyMatch?.value ?? null,
                name: companyMatch?.label ?? null,
                raw: apiStr,
            });
            const fieldListWithCompanyId = built.map((f: any) =>
                f.field === 'selectCompany' && companyMatch ? { ...f, value: companyMatch.value } : f
            );
            setUtilityCompanyFieldList(fieldListWithCompanyId);
            setBillDetailFieldList(buildBillDetailFieldList(utilityDetails));
            if (utilityDetails.SolarUtility.FileName != null && utilityDetails.SolarUtility.FileName !== '') {
                setUploadFiles([new File([], utilityDetails.SolarUtility.FileName)]);
            } else {
                setUploadFiles([]);
            }
            setIsFileChange(false);
            const { billDetails, utilityFile, annualUsageType, usageData } = buildUsageAndBillFromUtilityDetails(utilityDetails);
            setUsageTabData(usageData);
            setMonthlyUsage(usageData?.monthlyUsage?.data ?? null);
            handleYearlyUsageChange(usageData.yearlyUsage.data);
            setUtilityCompanyName(companyMatch ? companyMatch.label : null);
            const companyNameForExcel = companyMatch ? companyMatch.label : (apiStr && isNaN(Number(apiStr)) ? apiStr : '');
            const stopLoader = () => {
                didHydrateUtilityFormRef.current = true;
                setUtilityDetailsDirty(false);
                setLoader(false);
            };
            if (companyNameForExcel) {
                loadCascadingOptionsForCompany(companyNameForExcel)?.then(stopLoader).catch(stopLoader);
            } else {
                stopLoader();
            }
            const getTabIdx = (v: string) => { switch (v) { case 'DailyUsageCont': return '1'; case 'MonthlyUsageCont': return '2'; case 'YearlyUsageCont': return '3'; default: return '1'; } };
            setTabIndex(getTabIdx(annualUsageType));
        };

        masterServices.GetCompanies(stateCode, financePartner?.Finance_Partner_Id)
            .then((response) => {
                const actualData = response?.data ?? response;
                if (!Array.isArray(actualData)) {
                    setUtilityPanelError('Utility company list returned an unexpected response.');
                    setLoader(false);
                    return;
                }
                const companyList = actualData.map((company: any) => ({
                    value: String(company.Value),
                    label: company.excelValue,
                }));
                console.log(
                    'Utility Company list',
                    companyList.map((c) => ({ id: c.value, name: c.label })),
                );
                setCompanyList(companyList);
                if (solarUtilityData) {
                    applyUtilityDetails(solarUtilityData, companyList);
                    const companyIndex = companyList.findIndex((c: any) => String(c.value) === String(solarUtilityData.SolarUtility.UtilityCompany))
                    if(companyIndex < 0){
                        let field = utilityCompanyFields.find((f: any) => f.field === 'selectCompany');

                        if(field){
                            updateValue('utilityCompanyFieldList', utilityCompanyFieldList, field, companyList[0]?.value);
                            utlityCompanyUpdated(companyList[0])
                        } 
                    }

                } else {
                    designServices
                        .RetrieveUtilityInformation(numericOppId)
                        .then((result) => {
                            const { utilityDetails, annualUsageType, usageData } = result;
                            setSolarUtilityData(utilityDetails);
                            setUtilityInformation(utilityDetails);
                            const fieldConfig = getUtilityCompanyFieldConfig(
                                [
                                    ...(utilityDetails?.OpportunityProductsFinanceInfo || []),
                                    ...opportunityProducts,
                                ]
                            );
                            const built = buildUtilityCompanyFieldList(utilityDetails, fieldConfig);
                            const apiCompanyRaw = utilityDetails?.SolarUtility?.UtilityCompany;
                            const apiStr = String(apiCompanyRaw ?? '').trim();
                            const companyMatchByLabel = companyList.find((c: any) => String(c.label ?? '').trim() === apiStr);
                            const companyMatchByValue = companyList.find((c: any) => String(c.value ?? '') === apiStr);
                            const companyMatch = companyMatchByLabel || companyMatchByValue;
                            console.log('Utility Company mapped', {
                                id: companyMatch?.value ?? null,
                                name: companyMatch?.label ?? null,
                                raw: apiStr,
                            });
                            const fieldListWithCompanyId = built.map((f: any) =>
                                f.field === 'selectCompany' && companyMatch ? { ...f, value: companyMatch.value } : f
                            );
                            setUtilityCompanyFieldList(fieldListWithCompanyId);
                            setBillDetailFieldList(buildBillDetailFieldList(utilityDetails));
                            setUsageTabData(usageData);
                            setMonthlyUsage(usageData?.monthlyUsage?.data ?? null);
                            handleYearlyUsageChange(usageData.yearlyUsage.data);
                            const getTabIdx = (v: string) => {
                                switch (v) {
                                    case 'DailyUsageCont':
                                        return '1';
                                    case 'MonthlyUsageCont':
                                        return '2';
                                    case 'YearlyUsageCont':
                                        return '3';
                                    default:
                                        return '1';
                                }
                            };
                            setTabIndex(getTabIdx(annualUsageType));
                            setUtilityCompanyName(companyMatch ? companyMatch.label : null);
                            const companyNameForExcel = companyMatch
                                ? companyMatch.label
                                : apiStr && isNaN(Number(apiStr))
                                  ? apiStr
                                  : '';
                            const stopLoader = () => {
                                didHydrateUtilityFormRef.current = true;
                                setUtilityDetailsDirty(false);
                                setLoader(false);
                            };
                            if (companyNameForExcel) {
                                loadCascadingOptionsForCompany(companyNameForExcel)?.then(stopLoader).catch(stopLoader);
                            } else {
                                stopLoader();
                            }
                        })
                        .catch(() => {
                            setUtilityPanelError('Could not load saved utility information for this opportunity.');
                        });
                }
            })
            .catch(() => {
                setUtilityPanelError('Could not load utility companies for this state and finance partner.');
            });

        if(solarUtilityData?.SolarUtility?.DailyUsage  ){
            if(solarUtilityData.SolarUtility.DailyUsage.AnnualUsage && solarUtilityData.SolarUtility.DailyUsage.Data && solarUtilityData.SolarUtility.DailyUsage.Data.length > 0){
                setDailyUsageJson(solarUtilityData.SolarUtility.DailyUsage);
            } else {
                setDailyUsageJson({
                    AnnualUsage: solarUtilityData.SolarUtility.DailyUsage.AnnualUsage ?? 0,
                    Data: []
                });
            }
        }
    }, [open, opportunityDetails, opportunityID]);

    const visibleUtilityFields = utilityCompanyFieldList.filter((field) => !field.hidden);
    const companyRateFields = visibleUtilityFields.filter((field) =>
        ['selectState', 'State', 'selectCompany', 'selectCurrentRate', 'selectProposedRates', 'customerCharge', 'outstandingBalance'].includes(field.field)
    );
    const pricingOptionFields = visibleUtilityFields.filter((field) =>
        ['selectUtilityIncrease', 'currentAllInUtilityRate', 'utilityLIDRate'].includes(field.field)
    );
    const visibleBillFields = billDetailFieldList.filter((field) => !field.hidden);
    const billFieldsDocument = visibleBillFields.filter((f) => f.field === 'billFile');
    const billFieldsFromBill = visibleBillFields.filter((f) => f.field !== 'billFile');

    function renderUtilityCompanyField(field: any) {
        if (field.dataType === 'Dropdown') {
            const options = getOptions(field.field) || [];
            return (
                <div className="obd-utility__field">
                    <label className="obd-utility__field-label">
                        {field.displayText}
                        {field.require && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <OBDSelect
                        className="obd-utility__select"
                        popupClassName={`obd-select-dropdown obd-theme-${theme} obd-utility-details-select-dropdown`}
                        aria-label="Select est. annual utility increase"
                        placeholder="Select..."
                        value={field.value === '' || field.value == null ? undefined : field.value}
                        options={options}
                        onChange={(value) => utlityCompanyFieldUpdated(value ?? '', field)}
                        disabled={field.disbled || isViewOnly}
                    />
                    {(!field.value || field.value === '' || field.value === '0') && field.require && (
                        <span className="validation-message">{field.displayText} is required</span>
                    )}
                </div>
            );
        }
        if (field.dataType === 'Textbox') {
            const text = field.value == null ? '' : String(field.value).trim();
            const allInInvalid =
                field.field === 'currentAllInUtilityRate' &&
                field.require &&
                text !== '' &&
                text.toUpperCase() !== 'N/A' &&
                text !== '0' &&
                (!Number.isFinite(Number(text)) || Number(text) < 0.001);
            const requiredEmpty =
                field.require &&
                (text === '' || text === '0' || text.toUpperCase() === 'N/A');
            return (
                <div className="obd-utility__field">
                    <label className="obd-utility__field-label">
                        {field.displayText}
                        {field.require && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <input
                        type="text"
                        className="obd-utility__input"
                        value={field.value}
                        disabled={field.disbled || isViewOnly}
                        onChange={(e) => utlityCompanyFieldUpdated(e.target.value, field)}
                    />
                    {(requiredEmpty || allInInvalid) && (
                        <span className="validation-message">
                            {allInInvalid
                                ? `${field.displayText} must be greater than 0`
                                : `${field.displayText} is required`}
                        </span>
                    )}
                </div>
            );
        }
        if (field.dataType === 'Checkbox') {
            return (
                <div className="obd-utility__field obd-utility__field--checkbox">
                    <Checkbox
                        disabled={
                            isViewOnly ||
                            (field.field === 'utilityLIDRate' && isUtilityLidRateDisabled)
                        }
                        checked={!!field.value}
                        onChange={(e) => updateCheckboxFieldValue(field, e)}
                    >
                        <span className="obd-utility__checkbox-text">{field.displayText}</span>
                    </Checkbox>
                </div>
            );
        }
        return null;
    }

    function renderBillDetailField(field: any) {
        if (field.dataType === 'FileUpload') {
            return uploadFiles.length === 0 ? (
                <div className="obd-utility__field">
                    <label className="obd-utility__upload-wrap obd-utility-popup__upload">
                        <input
                            type="file"
                            className="obd-utility__upload-input"
                            accept=".pdf,.csv,.xlsx,.xls,image/*"
                            onChange={handleFileChange}
                            disabled={billExtractLoading || field.disbled || isOpportunityViewOnly}
                            aria-hidden
                        />
                        <span className="obd-utility__upload-zone">
                            <UploadSimple size={24} weight="duotone" className="obd-utility__upload-icon" aria-hidden />
                            <span className="obd-utility__upload-text">Drag & drop or click to upload.</span>
                        </span>
                    </label>
                    {billExtractError && (
                        <span className="obd-utility__field-error" style={{ color: '#ff4d4f', fontSize: 12 }}>
                            {billExtractError}
                        </span>
                    )}
                </div>
            ) : (
                <React.Fragment>
                    <ul className="obd-utility__file-preview">
                        {uploadFiles.map((file, index) => {
                            const key = `${file.name}-${file.size}-${file.lastModified}-${index}`;
                            const previewUrl = (file.type || '').startsWith('image/')
                                ? imagePreviewUrls[`${file.name}-${file.size}-${file.lastModified}`]
                                : null;
                            return (
                                <div className="obd-utility__upload-file-preview" key={key}>
                                    <li>
                                        {previewUrl ? (
                                            <img src={previewUrl} alt="" className="obd-utility__file-preview-thumb" />
                                        ) : (
                                            <span className="obd-utility__file-preview-icon-wrap">
                                                <FileIcon size={24} weight="duotone" aria-hidden />
                                            </span>
                                        )}
                                        <span className="obd-utility__file-preview-item-name" title={file.name}>
                                            {file.name}
                                        </span>
                                        <button
                                            type="button"
                                            className="obd-utility__file-preview-remove"
                                            onClick={() => removeUploadFile(index)}
                                            aria-label={`Remove ${file.name}`}
                                            disabled={isOpportunityViewOnly}
                                        >
                                            <X size={16} weight="bold" />
                                        </button>
                                    </li>
                                    {billExtractLoading && (
                                        <div className="obd-utility__upload-text-wrap">
                                            <Spin size="small" />
                                            <span
                                                className="obd-utility__upload-text"
                                                style={{ color: '#8383ff', fontSize: 14, marginTop: 10 }}
                                            >
                                                Extracting…
                                            </span>
                                        </div>
                                    )}
                                    {billExtractError && (
                                        <div className="obd-utility__upload-text-wrap">
                                            <span
                                                className="obd-utility__upload-text"
                                                style={{ color: '#ff4d4f', fontSize: 14, marginLeft: 10 }}
                                            >
                                                {billExtractError}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </ul>
                </React.Fragment>
            );
        }
        if (field.dataType === 'radioButton') {
            return (
                <div className="obd-utility__field">
                    <label className="obd-utility__field-label">{field.displayText}</label>
                    <Radio.Group
                        options={plainOptions}
                        disabled={field.disbled || isViewOnly}
                        value={field.value}
                        onChange={(e) => handleBillFieldChange(field, e.target.value)}
                    />
                </div>
            );
        }
        if (field.dataType === 'Datepicker') {
            return (
                <div className="obd-utility__field">
                    <label className="obd-utility__field-label">{field.displayText}</label>
                    <DatePicker
                        className="obd-utility__date-picker"
                        placeholder={field.displayText}
                        disabled={field.disbled || isViewOnly}
                        format="MM-DD-YYYY"
                        value={field.value ? dayjs(field.value, ['YYYY-MM-DD', 'MM-DD-YYYY']) : null}
                        onChange={(_, dateStr) => handleBillFieldChange(field, dateStr)}
                    />
                </div>
            );
        }
        if (field.dataType === 'Textbox') {
            return (
                <div className="obd-utility__field">
                    <label className="obd-utility__field-label">
                        {field.displayText}
                        {field.require && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <input
                        type="text"
                        className="obd-utility__input"
                        value={field.value}
                        disabled={field.disbled || isViewOnly}
                        onChange={(e) => handleBillFieldChange(field, e.target.value)}
                    />
                </div>
            );
        }
        return null;
    }

    if (!open) return null;

    const saveResponseStyle = (type: 'success' | 'error' | 'info'): React.CSSProperties => {
        if (type === 'success') {
            if (theme === 'dark') {
                return {
                    background: 'linear-gradient(165deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.14))',
                    border: '1px solid rgba(74, 222, 128, 0.4)',
                    color: '#4ade80',
                };
            }
            return {
                background: 'linear-gradient(165deg, rgba(34, 197, 94, 0.16), rgba(22, 163, 74, 0.12))',
                border: '1px solid rgba(22, 163, 74, 0.34)',
                color: '#16a34a',
            };
        }
        if (type === 'error') {
            return theme === 'dark'
                ? {
                      background: 'rgba(248, 113, 113, 0.1)',
                      border: '1px solid rgba(248, 113, 113, 0.35)',
                      color: 'rgba(254, 202, 202, 0.95)',
                  }
                : {
                      background: 'rgba(220, 38, 38, 0.08)',
                      border: '1px solid rgba(220, 38, 38, 0.22)',
                      color: '#b91c1c',
                  };
        }
        return theme === 'dark'
            ? {
                  background: 'rgba(96, 165, 250, 0.12)',
                  border: '1px solid rgba(96, 165, 250, 0.3)',
                  color: 'rgba(147, 197, 253, 0.95)',
              }
            : {
                  background: 'rgba(37, 99, 235, 0.08)',
                  border: '1px solid rgba(37, 99, 235, 0.2)',
                  color: 'var(--primary)',
              };
    };

    const loadCalculatorHeaderActions = (
        <div className="obd-metrics__action-group" role="group" aria-label="Utility actions">
            <button
                type="button"
                className="obd-merged-system__chip-btn"
                onClick={() => setLoadCalculatorOpen(true)}
                aria-label="Load Calculator"
            >
                <CalculatorIcon size={16} weight="bold" />
                <span>Load Calculator</span>
            </button>
        </div>
    );

    const utilityModalTitle = (
        <span className="obd-utility-details-popup__title">
            <ChartBar size={18} weight="duotone" className="obd-utility-details-popup__title-icon" aria-hidden />
            Utility Details
            {utilityDetailsDirty ? (
                <span
                    className="obd-utility__unsaved-chip obd-utility-details-popup__unsaved"
                    title="You have unsaved changes. Click Save to keep them."
                >
                    Unsaved
                </span>
            ) : null}
        </span>
    );

    return (
        <>
        <DashboardModal
            open={open}
            onClose={onClose}
            title={utilityModalTitle}
            headerActions={loadCalculatorHeaderActions}
            width="min(1600px, calc(100vw - 32px))"
            theme={theme}
            maskClosable
            rootClassName={`obd-theme-${theme} obd-utility-details-modal`}
            className="obd-utility-details-modal-shell"
            loading={isShowingLoader}
            footer={(
                <div
                    className="obd-utility-popup__footer obd-utility-details-modal__footer"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 12,
                        width: '100%',
                    }}
                >
                    {saveResponse && (
                        <div
                            role="alert"
                            aria-live="polite"
                            className={`obd-utility-popup__save-response obd-utility-details-modal__save-msg obd-utility-details-modal__save-msg--${saveResponse.type} ${
                                saveResponse.type === 'success' ? 'obd-utility-details-modal__save-msg--toast' : ''
                            }`}
                            style={{
                                ...saveResponseStyle(saveResponse.type),
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                marginRight: 'auto',
                                padding: '8px 12px',
                                borderRadius: 10,
                                lineHeight: 1.35,
                                fontSize: 13,
                                fontWeight: 600,
                            }}
                        >
                            {saveResponse.type === 'success' && (
                                <CheckCircle size={15} weight="fill" aria-hidden style={{ color: 'inherit', flexShrink: 0 }} />
                            )}
                            <span style={{ display: 'inline-flex', alignItems: 'center', color: 'inherit' }}>{saveResponse.message}</span>
                        </div>
                    )}
                    <div
                        className="obd-utility-details-modal__footer-actions"
                        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}
                    >
                        <span className="obd-utility-details-modal__footer-action">
                            <Button
                                className="obd-utility-details-modal__btn-close"
                                onClick={onClose}
                                disabled={isUploading || saveLoading}
                            >
                                <X size={14} weight="bold" aria-hidden />
                                Close
                            </Button>
                        </span>
                        {!isViewOnly && (
                            <span className="obd-utility-details-modal__footer-action">
                                <Button
                                    className="obd-utility-details-modal__btn-save"
                                    type="primary"
                                    loading={saveLoading}
                                    onClick={saveUtilityDetails}
                                    disabled={isUploading || saveLoading || billExtractLoading}
                                >
                                    {!saveLoading && <FloppyDisk size={14} weight="fill" aria-hidden />}
                                    {isUploading ? `Uploading... (${uploadProgress}%)` : 'Save'}
                                </Button>
                            </span>
                        )}
                    </div>
                </div>
            )}
        >
            {messageContextHolder}
            <div
                id='utiltiy-model-form-section'
                className="obd-modal obd-utility-popup"
                style={{ position: 'relative' }}
            >
                <div className="obd-modal__body obd-utility-popup__body">
                    {utilityPanelError && (
                        <div className="obd-utility-popup__alert obd-utility-details-modal__alert obd-utility-details-modal__alert--error" role="alert">
                            {utilityPanelError}
                        </div>
                    )}
                    <Tabs
                        className="obd-utility-popup__main-tabs"
                        activeKey={mainSectionTab}
                        onChange={setMainSectionTab}
                        destroyInactiveTabPane={false}
                        items={[
                            {
                                key: 'overview',
                                label: (
                                    <span className="obd-utility-popup__main-tab-inner">
                                        <ChartBar
                                            size={18}
                                            weight="duotone"
                                            className="obd-utility-popup__main-tab-icon"
                                            aria-hidden
                                        />
                                        <span className="obd-utility-popup__main-tab-label">Overview</span>
                                    </span>
                                ),
                                children: (
                                    <div className="obd-utility-popup__tab-panel obd-utility-popup__tab-panel--overview">
                                        <p className="obd-utility-popup__tab-intro">
                                            Usage, serving utility, uploaded files, and current all-in rate.
                                        </p>
                                        <UtilityDetailsOverviewPanel />
                                    </div>
                                ),
                            },
                            {
                                key: 'company',
                                label: (
                                    <span className="obd-utility-popup__main-tab-inner">
                                        <Lightning
                                            size={18}
                                            weight="duotone"
                                            className="obd-utility-popup__main-tab-icon"
                                            aria-hidden
                                        />
                                        <span className="obd-utility-popup__main-tab-label">Utility company</span>
                                    </span>
                                ),
                                children: (
                                    <div className="obd-utility-popup__tab-panel">
                                        <p className="obd-utility-popup__tab-intro">
                                            Serving utility, rate schedules, escalation, and optional program or charge fields.
                                        </p>
                                        <div className="obd-utility-popup__panel">
                                            <div className="obd-utility-popup__section">
                                                <div className="obd-utility-popup__subsection-headers-top" style={{ display: 'flex', gap: '40px', marginBottom: '20px', flexWrap: 'wrap' }}>
                                                    <div className="obd-utility-popup__custom-top-head" style={{ flex: '1 1 300px', margin: 0, minWidth: '240px', borderTop: 'none', border: 'none' }}>
                                                        <h4 className="obd-utility-popup__custom-top-title" style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '4px', borderTop: 'none', border: 'none' }}>Company &amp; rate plans</h4>
                                                        <p className="obd-utility-popup__custom-top-desc" style={{ fontSize: '12px', opacity: 0.7, margin: 0, lineHeight: '1.4', borderTop: 'none', border: 'none' }}>
                                                            Select the serving utility and current / post-install rate schedules.
                                                        </p>
                                                    </div>
                                                    <div className="obd-utility-popup__custom-top-head" style={{ flex: '1 1 300px', margin: 0, minWidth: '240px', borderTop: 'none', border: 'none' }}>
                                                        <h4 className="obd-utility-popup__custom-top-title" style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '4px', borderTop: 'none', border: 'none' }}>Rates &amp; program options</h4>
                                                        <p className="obd-utility-popup__custom-top-desc" style={{ fontSize: '12px', opacity: 0.7, margin: 0, lineHeight: '1.4', borderTop: 'none', border: 'none' }}>
                                                            Escalation assumptions, blended rate, and LID where applicable.
                                                        </p>
                                                    </div>
                                                </div>
                                                <Form layout="vertical" className="sd-utility-popup__form">
                                                    <div className="obd-utility-popup__subsection-block obd-utility-popup__subsection-block--ordered-fields">
                                                        {visibleUtilityFields.map((field) => (
                                                            <div
                                                                key={field.field}
                                                                className={
                                                                    field.dataType === 'Checkbox'
                                                                        ? 'sd-utility-popup__field sd-utility-popup__field--checkbox'
                                                                        : field.customClass === 'double-width'
                                                                        ? 'sd-utility-popup__field double-width'
                                                                        : 'sd-utility-popup__field'
                                                                }
                                                            >
                                                                {renderUtilityCompanyField(field)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Form>
                                            </div>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                key: 'bill',
                                label: (
                                    <span className="obd-utility-popup__main-tab-inner">
                                        <FileText
                                            size={18}
                                            weight="duotone"
                                            className="obd-utility-popup__main-tab-icon"
                                            aria-hidden
                                        />
                                        <span className="obd-utility-popup__main-tab-label">Bill detail</span>
                                    </span>
                                ),
                                children: (
                                    <div className="obd-utility-popup__tab-panel">
                                        <p className="obd-utility-popup__tab-intro">
                                            Upload a bill for extraction, then confirm reading dates, account data, and rates from the bill.
                                        </p>
                                        <div className="obd-utility-popup__panel">
                                            <div className="obd-utility-popup__section">
                                                <Form layout="vertical" className="sd-utility-popup__form">
                                                    {billFieldsDocument.length > 0 && (
                                                        <div className="obd-utility-popup__subsection-block obd-utility-popup__subsection-block--half">
                                                            <div className="obd-utility-popup__subsection-head" id="obd-utility-sub-bill-doc">
                                                                <h4 className="obd-utility-popup__subsection-title">Bill document</h4>
                                                                <p className="obd-utility-popup__subsection-desc">
                                                                    Upload a PDF or image of a recent bill to extract details automatically.
                                                                </p>
                                                            </div>
                                                            <div className="sd-utility-popup__field double-width">
                                                                {billFieldsDocument.map((field) => (
                                                                    <React.Fragment key={field.field}>{renderBillDetailField(field)}</React.Fragment>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {billFieldsFromBill.length > 0 && (
                                                        <div className="obd-utility-popup__subsection-block obd-utility-popup__subsection-block--half">
                                                            <div className="obd-utility-popup__subsection-head" id="obd-utility-sub-bill-meta">
                                                                <h4 className="obd-utility-popup__subsection-title">Reading &amp; account</h4>
                                                                <p className="obd-utility-popup__subsection-desc">
                                                                    Shutoff status, billed rate, meter and account identifiers, bill date.
                                                                </p>
                                                            </div>
                                                            {billFieldsFromBill.map((field) => (
                                                                <div key={field.field} className="sd-utility-popup__field">
                                                                    {renderBillDetailField(field)}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </Form>
                                            </div>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                key: 'usage',
                                label: (
                                    <span className="obd-utility-popup__main-tab-inner">
                                        <ChartLine
                                            size={18}
                                            weight="duotone"
                                            className="obd-utility-popup__main-tab-icon"
                                            aria-hidden
                                        />
                                        <span className="obd-utility-popup__main-tab-label">Annual usage</span>
                                    </span>
                                ),
                                children: (
                                    <div className="obd-utility-popup__tab-panel">
                                        <p className="obd-utility-popup__tab-intro">
                                            Match how usage appears on the bill: daily history, monthly totals, or one annual kWh figure.
                                        </p>
                                        <div className="obd-utility-popup__panel">
                                            <div className="obd-utility-popup__section">
                                                <div className="obd-utility-popup__subsection-block obd-utility-popup__subsection-block--full">
                                                    <div
                                                        className="obd-utility-popup__subsection-head"
                                                        id="obd-utility-sub-usage"
                                                    >
                                                        <h4 className="obd-utility-popup__subsection-title">Entry format</h4>
                                                        <p className="obd-utility-popup__subsection-desc">
                                                            Use Daily, Monthly, or Yearly depending on what your utility statement shows.
                                                        </p>
                                                    </div>
                                                    <div className="obd-utility-popup__section obd-utility-popup__section--usage-tabs">
                                                        {usageTabData && (
                                                            <Tabs
                                                                className="obd-utility-popup__tabs obd-utility-popup__tabs--nested"
                                                                activeKey={tabIndex}
                                                                onChange={(key) => {
                                                                    setTabIndex(key);
                                                                    markUtilityDirty();
                                                                }}
                                                                items={getAnnualUsageTabs(usageTabData)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>
        </DashboardModal>
        <UtilityLoadCalculatorModal
            open={loadCalculatorOpen}
            onClose={() => setLoadCalculatorOpen(false)}
            opportunityId={opportunityDetails?.Opportunity_Id}
        />
        <Modal
            title="Confirm utility change"
            open={changeOrderConfirmOpen}
            zIndex={13200}
            centered
            maskClosable
            okText="Continue"
            cancelText="Cancel"
            rootClassName={`obd-theme-${theme} obd-utility-change-order-confirm`}
            onOk={() => finishChangeOrderConfirm(true)}
            onCancel={() => void finishChangeOrderConfirm(false)}
            afterClose={() => {
                if (changeOrderConfirmResolveRef.current) {
                    changeOrderConfirmResolveRef.current(false);
                    changeOrderConfirmResolveRef.current = null;
                }
            }}
        >
            <p style={{ margin: 0 }}>
                You have updated the Utility Company for this opportunity. This will require a complete re-sign of the customer Agreement. 
                As pricing varies with Utility Company, your current quote and contract will be voided if you want to continue with the update of the Utility Company.
            </p>
        </Modal>
        </>
    );
};

export default UtilityDetailsModal;

